import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import { formatCurrency, formatCurrencyWithDot } from '@/libs/antd/formater';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { useUserData } from '@/libs/fleet-api/hooks';
import {
	useSelectFinancialReportPeriodType,
	useSelectFinancialReportAuditStatus,
	useSelectBankStatementType,
	useSelectMasterBank,
	useSelectFinancialReportPeriodTypeDetail,
} from '@/libs/fleet-api/hooks/master-data';
import {
	BankMasterData,
	MasterData,
} from '@/libs/fleet-api/services/master-data';
import {
	ApproveMPPPayload,
	GetMPPDetailParam,
	useSubmitMPPFCOMMutation,
	SubmitMPPAssetInformation,
	SubmitMPPBankStatement,
	SubmitMPPFinancialReport,
	SubmitMPPOtherDocument,
	SubmitMPPPayload,
	SubmitMPPTransactionDetailSummary,
	TimeValidation,
	useApproveMPPMutation,
	useGetMPPDetail,
	useGetSimulationDetail,
	useSubmitMPPMutation,
	validateMPPTiming,
} from '@/libs/fleet-api/services/mpp';
import { SPFCovenantPayload } from '@/libs/fleet-api/services/spf';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import {
	ArrowLeftOutlined,
	DeleteOutlined,
	EditOutlined,
	EyeOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
	Alert,
	Button,
	Col,
	Collapse,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Table,
	Tabs,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useEffect, useState } from 'react';
import { NAPNJFModal, useNAPNJFModal } from '../nap/NAPDownloadPage';
import { NAPJFModal, useNAPJFModal } from '../nap/NAPJFDownloadPage';
import CovenantModal, { useCovenantModal } from '../spf/CovenantModal';
import AssetModal, { AssetModalForm, useAssetModal } from './AssetModal';
import BankStatementModal, {
	useBankStatementModal,
} from './BankStatementModal';
import FinancialReportModal, {
	useFinancialReportModal,
} from './FinancialReportModal';
import KolektabilitasModal, {
	useKolektabilitasModal,
} from './KolektabilitasModal';
import OtherDocumentModal, {
	useOtherDocumentModal,
} from './OtherDocumentModal';
import SimulationModal, { useSimulationModal } from './SimulationModal';
import SPKDocumentModal, { useSPKDocumentModal } from './SPKDocumentModal';

type MainForm = Omit<SubmitMPPPayload, 'transactionDetailSummary'> &
	Partial<ApproveMPPPayload> & {
		transactionDetailSummary: TransactionForm[];
	};

type TransactionData = {
	assetType: string;
	productId: string;
	brand: string;
	totalUnit: number;
	totalNtf: number;
	totalDownPayment: number;
	totalInstallment: number;
	insuranceType: string;
	insuranceAmount: number;
	simulationId: number;
	creditSimulationNo: string;
	creditSimulationId: number;
	calculationPerformed?: boolean;
	segmentIndustry: string;
	financingProduct: MasterData;
};

type TransactionForm = SubmitMPPTransactionDetailSummary &
	TransactionData & {
		bank: BankMasterData;
		assetInformations: AssetModalForm[];
		hasDealerRefund?: boolean;
		dealerRefundAmount?: number;
	};

type MPPPageProps = GetMPPDetailParam & {
	onBack?: () => void;
	viewOnly?: boolean;
	isReturn?: boolean;
};

const MPPPage: FC<MPPPageProps> = (props) => {
	const { onBack, viewOnly, isReturn } = props;
	const { groupName } = useUserData() ?? {};
	const isReviewing = groupName === 'FBDH' || groupName === 'CCDH';

	const { modal } = useModals();
	const { navigate } = useApp();
	const { openCustomerDetail, openCustomerGroupDetail, openPreviewModal } =
		useModals();

	const [view, setView] = useState<
		| {
				type: 'main';
		  }
		| {
				type: 'new-transaction';
				data?: TransactionData;
		  }
		| {
				type: 'view-transaction';
				data?: TransactionData;
		  }
		| {
				type: 'edit-transaction';
				data: TransactionForm;
				index: number;
		  }
	>({
		type: 'main',
	});

	const mppDetailQuery = useGetMPPDetail(
		'mppId' in props ? { mppId: props.mppId } : { spfId: props.spfId },
	);

	const simulationDetail = useGetSimulationDetail();

	const mainForm = useForm<MainForm>();
	const transactionForm = useForm<TransactionForm>();

	const submitMPPMutation = useSubmitMPPMutation();
	const submitMPPFCOMMutation = useSubmitMPPFCOMMutation();
	const approveMPPMutation = useApproveMPPMutation();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const covenantModal = useCovenantModal();
	const simulationModal = useSimulationModal();
	const assetModal = useAssetModal();
	const financialReportModal = useFinancialReportModal();
	const bankStatementModal = useBankStatementModal();
	const otherDocumentModal = useOtherDocumentModal();
	const spkDocumentModal = useSPKDocumentModal();
	const kolektabilitasModal = useKolektabilitasModal();
	const napNJFModal = useNAPNJFModal();
	const napJFModal = useNAPJFModal();
	const userData = useUserData();

	const selectMasterBank = useSelectMasterBank(true);
	const selectFinancialReportPeriodType =
		useSelectFinancialReportPeriodType(true);
	const selectFinancialReportAuditStatus =
		useSelectFinancialReportAuditStatus(true);
	const selectBankStatementType = useSelectBankStatementType(true);
	const selectFinancialReportPeriodTypeDetail =
		useSelectFinancialReportPeriodTypeDetail(undefined, true);

	// Helper function to get period type detail label by ID
	const getPeriodTypeDetailLabel = (periodTypeDetailId: number) => {
		if (!selectFinancialReportPeriodTypeDetail.options) return null;

		// Search through all loaded period type detail options to find the matching ID
		for (const option of selectFinancialReportPeriodTypeDetail.options) {
			if (option.value === periodTypeDetailId) {
				return option.label;
			}
		}
		return null;
	};

	const [dataTab, setDataTab] = useState<
		| 'Laporan Keuangan'
		| 'Laporan Penjualan'
		| 'Data Rekening Koran'
		| 'Dokumen Tambahan'
	>('Laporan Keuangan');

	const [timingValidation, setTimingValidation] =
		useState<TimeValidation | null>(null);

	// Initialize timing validation when component loads
	useEffect(() => {
		if (
			mppDetailQuery.data?.data?.facilityNotification?.[0]
				?.facilityValidityPeriod &&
			mppDetailQuery.data?.data?.createdAt
		) {
			const approvalDate = new Date(
				mppDetailQuery.data.data.facilityNotification[0].facilityValidityPeriod,
			);
			const mppCreationDate = new Date(mppDetailQuery.data.data.createdAt);
			const timing = validateMPPTiming(approvalDate, mppCreationDate);
			setTimingValidation(timing);
		}
	}, [mppDetailQuery.data]);

	// Set transaction form values when view data changes
	useEffect(() => {
		if (
			(view.type === 'new-transaction' || view.type === 'edit-transaction') &&
			view.data
		) {
			transactionForm.form.setFieldValue(
				transactionForm.fieldName('simulationId'),
				view.data.simulationId,
			);
			transactionForm.form.setFieldValue(
				transactionForm.fieldName('creditSimulationId'),
				view.data.creditSimulationId,
			);
			transactionForm.form.setFieldValue(
				transactionForm.fieldName('creditSimulationNo'),
				view.data.creditSimulationNo,
			);
		}
	}, [view, transactionForm]);

	// Enhanced error message mapping
	const getErrorMessage = (error: {
		status?: number;
		message?: string;
	}): string => {
		const errorMap = {
			400: 'Data tidak valid. Mohon periksa kembali form yang telah diisi.',
			401: 'Sesi anda telah berakhir. Silakan login kembali.',
			403: 'Anda tidak memiliki akses untuk melakukan operasi ini.',
			409: 'Terjadi konflik data. MPP mungkin sedang diproses oleh user lain.',
			422: 'Data tidak lengkap. Mohon lengkapi semua field yang diperlukan.',
			500: 'Terjadi kesalahan server. Silakan coba lagi atau hubungi administrator.',
			503: 'Layanan sedang tidak tersedia. Silakan coba beberapa saat lagi.',
		};

		// Check for network/timeout errors
		if (
			error?.message?.includes('timeout') ||
			error?.message?.includes('network')
		) {
			return 'Koneksi bermasalah. Mohon periksa koneksi internet dan coba lagi.';
		}

		// Check for validation errors
		if (error?.message?.includes('validation')) {
			return 'Terdapat kesalahan validasi data. Periksa kembali input anda.';
		}

		return (
			errorMap[error?.status as keyof typeof errorMap] ||
			'Terjadi kesalahan saat submit MPP. Silakan coba lagi.'
		);
	};

	// Validate form data before submission
	const validateFormData = (
		values: MainForm,
	): { isValid: boolean; errors: string[] } => {
		const errors: string[] = [];

		// Check 6-month rule if SPF approval date is available
		// if (
		// 	mppDetailQuery.data?.data?.facilityNotification?.[0]
		// 		?.facilityValidityPeriod
		// ) {
		// 	const approvalDate = new Date(
		// 		mppDetailQuery.data.data.facilityNotification[0].facilityValidityPeriod,
		// 	);
		// 	const timing = validateMPPTiming(approvalDate);
		// 	setTimingValidation(timing);

		// 	if (timing.requiresExtendedProcess && !values.mppCompletenessDataData) {
		// 		errors.push(
		// 			'Diperlukan kelengkapan data tambahan karena telah melewati 6 bulan sejak persetujuan SPF',
		// 		);
		// 	}
		// }

		// Validate transaction details
		if (
			!values.transactionDetailSummary ||
			values.transactionDetailSummary.length === 0
		) {
			errors.push('Minimal satu transaksi detail harus diisi');
		}

		// Validate aging date
		if (!values.agingDate) {
			errors.push('Tanggal aging data wajib diisi');
		}

		// Validate realization background
		if (
			!values.realizationBackground ||
			values.realizationBackground.trim().length === 0
		) {
			errors.push('Latar belakang realisasi wajib diisi');
		}

		return { isValid: errors.length === 0, errors };
	};

	if (mppDetailQuery.isFetching || simulationDetail.isPending) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const {
		customerName,
		customerId,
		bmppGroupName,
		bmppGroupId,
		industryClass,
		portfolioGuideline,
		ultimateOwner,
		createdAt,
		mklNo,
		covenants = [],
		facilities,
		agingDate,
		orderSource,
		backgroundRealisation,
		transactionDetailSummary,
		facilityNotification,
		prospectId,
		approvalHistory,
		customerFacility,
		customerFacilitySummary,
		customerAffiliatesFacility,
		customerAffiliateFacilitySummary,
		obligorDetail: backendObligorDetail,
		napPlafonTypeId,
		resolutionFile,
		approvalActionOptions = [],
		categoryOptions = [],
		priorityOptions = [],
		customRepeatFrequencyOptions = [],
		financingPurpose = [
			{
				value: 'Operasional',
				id: 1,
			},
			{
				value: 'Commercial',
				id: 2,
			},
		],
		repeatFrequencyOptions = [],
		monitoringType = [],
		furtherAnalysisOptions = [],
		positionOptions = [],
		monthOptions = [],
		dayOfWeekOptions = [],
		financingLimitTypeOptions = [],
		plafonTypeOptions = [],
		wayOfPaymentOptions = [],
		financialReports = [],
		financialReportAnalysisResult,
		salesReports,
		salesReportAnalysisResult,
		bankStatements = [],
		bankStatementAnalysisResult,
		otherDocuments = [],
		slikInquiryId,
	} = mppDetailQuery.data?.data ?? {};

	// Calculate obligor detail dynamically for FRM role in edit mode
	const obligorDetail = (() => {
		const isFRM = userData?.groupName === 'FRM';
		const shouldCalculateDynamically = isFRM && (!viewOnly || !isReviewing);

		if (shouldCalculateDynamically) {
			// Calculate based on current form data
			const value = mainForm.value;

			// Calculate total pengajuan customer (current transaction)
			const currentTransactionTotal = value?.transactionDetailSummary?.reduce(
				(acc, transaction) => {
					const unitAmount =
						transaction.assetInformations?.reduce(
							(unitAcc, asset) => unitAcc + (asset.unitAmount || 0),
							0,
						) || 0;

					acc.totalNtf += (transaction.totalNtf || 0) * unitAmount;
					acc.totalUnit += unitAmount;
					acc.totalInstallment = 0;
					acc.totalOutstanding = 0;

					return acc;
				},
				{ totalNtf: 0, totalUnit: 0, totalInstallment: 0, totalOutstanding: 0 },
			) || {
				totalNtf: 0,
				totalUnit: 0,
				totalInstallment: 0,
				totalOutstanding: 0,
			};

			const total = [
				// ...(customerFacility?.existingFacilities ?? []),
				...(customerFacility?.pendingFacilities ?? []),
			].reduce(
				(acc, curr) => {
					acc.unit += curr.unitCount;
					acc.ntf += curr.ntf;
					acc.installment += curr.installment;
					acc.outstanding += curr.outstanding;
					return acc;
				},
				{
					unit: currentTransactionTotal.totalUnit,
					ntf: currentTransactionTotal.totalNtf,
					installment: currentTransactionTotal.totalInstallment,
					outstanding: currentTransactionTotal.totalOutstanding,
				},
			);
			const totalExistingOutstanding =
				customerFacilitySummary?.totalOutstanding ?? 0;
			// const totalExistingNtf =
			// 	customerFacility?.existingFacilities?.reduce(
			// 		(acc, curr) => acc + curr.ntf,
			// 		0,
			// 	) ?? 0;

			const totalCustomerOutstanding =
				total.outstanding + totalExistingOutstanding;

			const totalCustomerSubmission = total.ntf + totalCustomerOutstanding;

			// Grand Total Pengajuan (One Obligor) = Total Pengajuan Customer + Afiliasi Customer
			const grandTotalApplication =
				totalCustomerSubmission +
				(customerAffiliateFacilitySummary?.totalOutstanding || 0);
			// (backendObligorDetail?.grandTotalApplication || 0);

			// Get plafond yang disetujui from backend e-star API (always use backend data)
			const grandTotalPlafon = backendObligorDetail?.grandTotalPlafon || 0;

			// Sisa plafond = plafond yang disetujui - total pengajuan customer saat ini
			const grandTotalRemainingPlafon =
				grandTotalPlafon - grandTotalApplication;

			return {
				grandTotalApplication,
				grandTotalPlafon,
				grandTotalRemainingPlafon,
			};
		}
		// For view-only mode or non-FRM roles, use backend data
		return backendObligorDetail;
	})();

	const statusOptions = [
		{
			id: 'done',
			value: 'Done',
		},
		{
			id: 'pending',
			value: 'Pending',
		},
	] as unknown as MasterData[];

	const isAbsolute =
		dayjs(createdAt).isBefore(dayjs().subtract(6, 'month')) ||
		Boolean(localStorage.getItem('isAbsolute'));

	const renderMainForm = () => {
		const { form, fieldName, value } = mainForm;

		const submissions =
			value?.transactionDetailSummary?.map((i) => {
				const unitAmount = i.assetInformations.reduce(
					(acc, curr) => acc + curr.unitAmount,
					0,
				);

				const totalNtf = i.totalNtf * unitAmount;
				const totalInstallment = i.totalInstallment * unitAmount;
				const totalOutstanding = 0;

				return {
					unitAmount,
					totalNtf,
					totalInstallment,
					totalOutstanding,
				};
			}) ?? [];

		const submissionTotal = submissions.reduce(
			(acc, i) => {
				acc.unit += i.unitAmount;
				acc.ntf += i.totalNtf;
				acc.installment += i.totalInstallment;
				acc.outstanding += i.totalOutstanding;
				return acc;
			},
			{
				unit: 0,
				ntf: 0,
				installment: 0,
				outstanding: 0,
			},
		);

		const total = [
			// ...(customerFacility?.existingFacilities ?? []),
			...(customerFacility?.pendingFacilities ?? []),
		].reduce(
			(acc, curr) => {
				acc.unit += curr.unitCount;
				acc.ntf += curr.ntf;
				acc.installment += curr.installment;
				acc.outstanding += curr.outstanding;
				return acc;
			},
			{
				unit: submissionTotal.unit,
				ntf: submissionTotal.ntf,
				installment: submissionTotal.installment,
				outstanding: submissionTotal.outstanding,
			},
		);
		const totalExistingOutstanding =
			customerFacilitySummary?.totalOutstanding ?? 0;
		// const totalExistingNtf =
		// 	customerFacility?.existingFacilities?.reduce(
		// 		(acc, curr) => acc + curr.ntf,
		// 		0,
		// 	) ?? 0;

		const totalCustomerOutstanding =
			total.outstanding + totalExistingOutstanding;

		const totalInstallment = total.installment + submissionTotal.installment;
		const totalCustomerSubmission = total.ntf + totalCustomerOutstanding;

		const totalPlafondApproved = customerFacilitySummary?.totalPlafon ?? 0;
		const totalRemainingPlafon = totalPlafondApproved - totalCustomerSubmission;

		return (
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				hidden={view.type !== 'main'}
				disabled={viewOnly}
				labelCol={{ span: 6 }}
				wrapperCol={{ span: 18 }}
				onFinish={async (values) => {
					// Transform bank objects to bankId numbers before submission
					const transformedValues = {
						...values,
						mppCompletenessDataData: {
							...values.mppCompletenessDataData,
							bankStatements:
								values.mppCompletenessDataData?.bankStatements
									?.map((statement) => ({
										...statement,
										bankId:
											statement.bankId &&
											typeof statement.bankId === 'object' &&
											'id' in statement.bankId
												? (statement.bankId as { id: number }).id
												: typeof statement.bankId === 'number'
													? statement.bankId
													: 0, // Default fallback for invalid bankId
									}))
									.filter((statement) => statement.bankId > 0) ?? [],
						},
					};

					if (isReviewing) {
						const actionWording = approvalActionOptions.find(
							(option) => (option.id as unknown as string) === values.action,
						)?.value;

						const isConfirmed = await modal.confirm({
							title: `${actionWording} memo pencairan pembiayaan?`,
							okText: 'YA',
							cancelText: 'TIDAK',
						});

						if (!isConfirmed) return;
						const approvalRs = await approveMPPMutation.mutateAsync({
							mppId: transformedValues.mppId!,
							action: transformedValues.action,
							notes: transformedValues.notes,
						});

						if (approvalRs.data) {
							await modal.success({
								title: 'Surat Persetujuan Fasilitas Berhasil Dibuat!',
								okText: 'BACK',
								subTitle: (
									<Fragment>
										<span>
											Berikut adalah nomor Surat Persetujuan Fasilitas Debitur
											Anda:
										</span>
										<div style={{ fontSize: 32, color: 'black' }}>
											{approvalRs.data}
										</div>
									</Fragment>
								),
							});
						}

						navigate('MPPListPage');
					} else {
						// Enhanced pre-submission validation
						const validation = validateFormData(transformedValues);

						if (!validation.isValid) {
							await modal.confirm({
								title: 'Validasi Data Gagal',
								subTitle: (
									<div>
										<p>Terdapat kesalahan pada data yang diinput:</p>
										<ul style={{ textAlign: 'left', paddingLeft: 20 }}>
											{validation.errors.map((error, index) => (
												<li key={index}>{error}</li>
											))}
										</ul>
									</div>
								),
								okText: 'OK',
								cancelButtonProps: { style: { display: 'none' } },
							});
							return;
						}

						// Show timing warning if extended process is required
						if (timingValidation?.requiresExtendedProcess) {
							const proceedWithExtended = await modal.confirm({
								title: 'Proses Extended Required',
								subTitle: `Telah melewati ${timingValidation.daysSinceSpfApproval} hari sejak persetujuan SPF. MPP akan memerlukan proses extended dengan kelengkapan data tambahan.`,
								okText: 'Lanjutkan',
								cancelText: 'Batal',
							});

							if (!proceedWithExtended) return;
						}

						const isConfirmed = await modal.confirm({
							title: `Apakah Anda yakin ingin ${groupName === 'CCA' ? 'verifikasi' : 'submit'} memo pencairan pembiayaan?`,
							okText: 'YA',
							cancelText: 'TIDAK',
						});

						if (!isConfirmed) return;

						try {
							// if return, resubmit using FCOM endpoint
							if (isReturn) {
								await submitMPPFCOMMutation.mutateAsync(transformedValues);
								await modal.success({
									title: 'MPP berhasil disubmit!',
									subTitle:
										'Memo pencairan pembiayaan telah berhasil dikirim untuk diproses oleh FCOM',
									okText: 'OK',
								});
							} else {
								await submitMPPMutation.mutateAsync(transformedValues);
								await modal.success({
									title: 'MPP berhasil disubmit!',
									subTitle:
										'Memo pencairan pembiayaan telah berhasil dikirim untuk diproses.',
									okText: 'OK',
								});
							}
							navigate('MPPListPage');
						} catch (error) {
							console.error('MPP submission error:', error);

							// Use enhanced error message mapping
							const errorMessage = getErrorMessage(
								error as { status?: number; message?: string },
							);

							await modal.confirm({
								title: 'Gagal Submit MPP',
								subTitle: errorMessage,
								okText: 'OK',
								cancelButtonProps: { style: { display: 'none' } },
							});
						}
					}
				}}
				onFinishFailed={(e) => {
					const fieldNames = e.errorFields?.[0].name ?? [];
					if (fieldNames.includes('financialReportAnalysisResult')) {
						setDataTab('Laporan Keuangan');
					}

					if (
						fieldNames.includes('salesReports') ||
						fieldNames.includes('salesReportAnalysisResult')
					) {
						setDataTab('Laporan Penjualan');
					}

					if (fieldNames.includes('bankStatementAnalysisResult')) {
						setDataTab('Data Rekening Koran');
					}
				}}
			>
				<Form.Item
					name={fieldName('id')}
					initialValue={'mppId' in props ? props.mppId : undefined}
				/>
				<Form.Item
					name={fieldName('mppId')}
					initialValue={'mppId' in props ? props.mppId : undefined}
				/>
				<Form.Item
					name={fieldName('spfId')}
					initialValue={'spfId' in props ? props.spfId : undefined}
				/>

				{/* 6-Month Rule Timing Validation Alert
				{timingValidation && (
					<Alert
						style={{ marginBottom: 16 }}
						type={timingValidation.requiresExtendedProcess ? 'warning' : 'info'}
						message={
							timingValidation.requiresExtendedProcess
								? 'Proses Extended Required'
								: 'Proses Standard'
						}
						description={
							<div>
								<p>
									{timingValidation.daysSinceSpfApproval} hari telah berlalu
									sejak persetujuan SPF.
								</p>
								{timingValidation.requiresExtendedProcess && (
									<div>
										<p>
											<strong>Dokumen tambahan yang diperlukan:</strong>
										</p>
										<ul style={{ paddingLeft: 20, margin: 0 }}>
											{timingValidation.additionalDocumentsRequired.map(
												(doc, index) => (
													<li key={index}>
														{doc.replace('_', ' ').toUpperCase()}
													</li>
												),
											)}
										</ul>
									</div>
								)}
							</div>
						}
						showIcon
						closable
					/>
				)} */}

				<Collapse
					defaultActiveKey={
						isAbsolute
							? ['covenant', 'facility', 'memo', 'data']
							: ['covenant', 'facility', 'memo']
					}
					items={[
						{
							key: 'covenant',
							label: 'Informasi Dokumen Covenant',
							children: (
								<Form.Item
									name={fieldName('covenants')}
									initialValue={
										covenants?.map((record) => {
											return {
												categoryId: record.category.id,
												covenant: record.covenant,
												dueDate: record.dueDate,
												furtherAnalysisId: record.furtherAnalysis.id,
												monitoringTypeId: record.monitoringType.id,
												priorityId: record.priority.id,
												repeatFrequencyId: record.repeatFrequency.id,
												covenantDocument: record.covenantDocument && {
													id: record.covenantDocument.id,
													name: record.covenantDocument.name,
													path: record.covenantDocument.path,
													url: record.covenantDocument.url,
												},
												id: record.id,
												repeatFrequencyCustom: record.repeatFrequencyCustom && {
													id: record.repeatFrequencyCustom.id,
													repeatFrequencyId:
														record.repeatFrequencyCustom.repeatFrequency.id,
													repeatValue: record.repeatFrequencyCustom.repeatValue,
													onPattern: record.repeatFrequencyCustom.onPattern && {
														dayOfMonth:
															record.repeatFrequencyCustom.onPattern.dayOfMonth,
														monthId:
															record.repeatFrequencyCustom.onPattern.month.id,
													},
													onThePattern: record.repeatFrequencyCustom
														.onThePattern && {
														dayOfWeekId:
															record.repeatFrequencyCustom.onThePattern
																.dayOfWeek.id,
														monthId:
															record.repeatFrequencyCustom.onThePattern.month
																.id,
														positionId:
															record.repeatFrequencyCustom.onThePattern.position
																.id,
													},
												},
												notes: record.notes,
												uploadDocumentDate: record.uploadDocumentDate,
												statusId: record.status,
											} as SPFCovenantPayload;
										}) ?? []
									}
									noStyle
								>
									{isAbsolute && (
										<Alert
											type="info"
											style={{ marginBottom: 20 }}
											message={
												<strong style={{ color: '#E10101' }}>
													Dokumen covenant debitur Anda belum lengkap. Mohon
													lengkapi dokumen covenant pada menu monitoring
													covenant{' '}
												</strong>
											}
										/>
									)}
									<Table
										scroll={{ x: 'max-content' }}
										bordered
										pagination={false}
										dataSource={
											value?.covenants?.map((record, index) => {
												return {
													...record,
													key: index + 1,
												};
											}) ?? []
										}
										columns={[
											{
												title: 'No',
												fixed: 'left',
												align: 'center',
												render: (_, __, index) => index + 1,
											},
											{
												hidden: groupName !== 'FRM',
												fixed: 'left',
												title: 'Edit',
												align: 'center',
												render: (_, record, index) => {
													if (groupName === 'CCA' && !record.covenantDocument?.id) {
														return (
															<EditOutlined style={{ color: '#bababa' }} />
														);
													}
													return (
														<a
															onClick={async (e) => {
																e.preventDefault();
																if (viewOnly) return;

																const updatedCovenant =
																	await covenantModal.open({
																		action:
																			groupName === 'CCA' ? 'edit-all' : 'edit',
																		record:
																			record as unknown as SPFCovenantPayload,
																	});

																if (!updatedCovenant) return;
																form.setFieldValue(
																	fieldName('covenants'),
																	(value?.covenants ?? []).map(
																		(record, recordIdx) =>
																			index === recordIdx
																				? updatedCovenant
																				: record,
																	),
																);
															}}
															style={{
																color: viewOnly ? '#bababa' : '#1557BC',
															}}
														>
															<EditOutlined />
														</a>
													);
												},
											},
											{
												title: 'Covenant',
												render: (_, record) => record.covenant,
											},
											{
												title: 'Kategori',
												render: (_, record) =>
													categoryOptions?.find(
														(option) => option.id === record.categoryId,
													)?.value ?? '-',
											},
											{
												title: 'Priority',
												render: (_, record) =>
													priorityOptions?.find(
														(option) => option.id === record.priorityId,
													)?.value ?? '-',
											},
											{
												title: 'Repeat',
												render: (_, record) =>
													repeatFrequencyOptions?.find(
														(option) => option.id === record.repeatFrequencyId,
													)?.value ?? '-',
											},
											{
												title: 'Jenis Monitoring',
												render: (_, record) =>
													monitoringType?.find(
														(option) => option.id === record.monitoringTypeId,
													)?.value ?? '-',
											},
											{
												title: 'Analisa Lebih Lanjut',
												render: (_, record) =>
													furtherAnalysisOptions?.find(
														(option) => option.id === record.furtherAnalysisId,
													)?.value ?? '-',
											},
											{
												title: 'Tanggal Upload Dokumen',
												render: (_, record) =>
													record.uploadDocumentDate
														? dayjs(record.uploadDocumentDate).format(
																'DD/MM/YYYY',
															)
														: '-',
											},
											{
												title: 'View Dokumen',
												fixed: 'right',
												align: 'center',
												render: (_, record) => (
													<a
														onClick={(e) => {
															e.preventDefault();
															if (!record.covenantDocument?.id) return;
															openPreviewModal(record.covenantDocument);
														}}
													>
														<FileSearchOutlined
															style={{
																color: record.covenantDocument?.id
																	? '#1557BC'
																	: '#bababa',
															}}
														/>
													</a>
												),
											},
											{
												title: 'Due Date',
												render: (_, record) =>
													record.dueDate
														? dayjs(record.dueDate).format('DD/MM/YYYY')
														: '-',
											},
											{
												title: 'OD',
												render: (_, __, recordIndex) => {
													const od = covenants?.find(
														(_, index) => index === recordIndex,
													)?.overdueDays;

													return typeof od === 'number' ? `${od} Hari` : '-';
												},
											},
											{
												title: 'Keterangan',
												render: (_, record) => record.notes ?? '-',
											},
										]}
									/>
								</Form.Item>
							),
						},
						{
							key: 'facility',
							label: 'Informasi Approval Pemberitahuan Fasilitas',
							children: (
								<Fragment>
									{facilityNotification?.map((record, index) => {
										const isMultiguna = record.financingTypes.some(
											(d) => d.value === 'Pembiayaan Multiguna',
										);

										return (
											<Fragment key={index}>
												<div style={{ marginBottom: 10 }}>
													<strong>Limit Plafon {index + 1}</strong>
												</div>
												<Table
													bordered
													pagination={false}
													dataSource={
														[
															{
																subject: 'Jenis Pembiayaan',
																approval:
																	record.financingTypes
																		?.map((type) => type.value)
																		?.join(', ') ?? '-',
															},
															{
																subject: 'Jenis Fasilitas',
																approval:
																	record.facilityTypes
																		?.map((type) => type.value)
																		?.join(', ') ?? '-',
															},
															{
																subject: 'Limit Pembiayaan',
																approval: record.approvedLimit
																	? formatCurrency(record.approvedLimit)
																	: '-',
															},
															{
																subject: 'Sifat Limit Pembiayaan',
																approval:
																	financingLimitTypeOptions?.find(
																		(option) =>
																			option.id === record.financingLimitTypeId,
																	)?.value ?? '-',
															},
															{
																subject: 'Jenis Plafon',
																approval:
																	plafonTypeOptions?.find(
																		(option) =>
																			option.id === record.plafonTypeId,
																	)?.value ?? '-',
															},
															{
																subject: 'Way Of Payment',
																approval:
																	wayOfPaymentOptions?.find(
																		(option) =>
																			option.id === record.wayOfPaymentId,
																	)?.value ?? '-',
															},
															{
																subject: 'Masa Berlaku Fasilitas',
																approval: record.facilityValidityPeriod
																	? dayjs(record.facilityValidityPeriod).format(
																			'DD/MM/YYYY',
																		)
																	: '-',
															},
															...(isMultiguna
																? [
																		{
																			subject: 'Mekanisme Pencairan',
																			approval:
																				record.disbursementMechanism ?? '-',
																		},
																		{
																			subject: 'Kondisi Objek Pembiayaan',
																			approval:
																				record.financingObjectCondition ?? '-',
																		},
																		{
																			subject: 'Penilaian Objek Pembiayaan',
																			approval:
																				record.financingObjectEvaluation ?? '-',
																		},
																	]
																: []),
														] as Record<'subject' | 'approval', string>[]
													}
													columns={[
														{
															title: 'Perihal',
															width: '50%',
															render: (_, record) => record.subject,
														},
														{
															title: 'Keterangan Approval',
															width: '50%',
															render: (_, record) => <b>{record.approval}</b>,
														},
													]}
												/>
												<Divider />
											</Fragment>
										);
									})}
									<Typography.Title level={3}>
										Detail Fasilitas
									</Typography.Title>
									<Table
										dataSource={facilities ?? []}
										columns={[
											{
												title: 'No',
												align: 'center',
												render: (_, __, index: number) => index + 1,
											},
											{
												title: 'Jenis Asset',
												render: (_, record) => record.assetType?.value,
											},
											{
												title: 'Kondisi Obyek Pembiayaan',
												render: (_, record) =>
													record.financingObjectCondition?.value,
											},
											{
												title: 'Sifat Limit Pembiayaan',
												render: (_, record) =>
													record.financingLimitType?.value ?? '-',
											},
											{
												title: 'Jenis Plafon',
												render: (_, record) => record.plafonType?.value,
											},
											{
												title: 'Tenor Pembiayaan',
												render: (_, record) => record.tenor?.value,
											},
											{
												title: 'Indikasi Suku Bunga (Eff)',
												render: (_, record) =>
													record.interestRateIndicationPercent
														? `${formatCurrencyWithDot(record.interestRateIndicationPercent)}%`
														: '-',
											},
											{
												title: 'Down Payment',
												render: (_, record) =>
													record.downPaymentPercent
														? `${formatCurrencyWithDot(record.downPaymentPercent)}%`
														: '-',
											},
											{
												title: 'Security Deposit',
												render: (_, record) =>
													record.securityDeposit
														? formatCurrency(record.securityDeposit)
														: '-',
											},
											{
												title: 'Biaya Administrasi',
												render: (_, record) =>
													record.administrativeFee
														? formatCurrency(record.administrativeFee)
														: '-',
											},
											{
												title: 'Provisi',
												render: (_, record) =>
													record.provisionFee
														? formatCurrencyWithDot(record.provisionFee) + '%'
														: '-',
											},
											{
												title: 'Biaya Layanan',
												render: (_, record) =>
													record.serviceFee
														? `${formatCurrencyWithDot(record.serviceFee)}%`
														: '-',
											},
											{
												title: 'Asuransi',
												render: (_, record) => record.insurance?.value ?? '-',
											},
											{
												title: 'Diskon Asuransi',
												align: 'center',
												render: (_, record) =>
													record.insuranceDiscountPercentage
														? `${formatCurrencyWithDot(record.insuranceDiscountPercentage)}%`
														: '-',
											},
											{
												title: 'Fidusia',
												render: (_, record) =>
													record.fiduciaryFee
														? formatCurrency(record.fiduciaryFee)
														: '-',
											},
										]}
										bordered
										pagination={false}
										scroll={{ x: 'max-content' }}
									/>
								</Fragment>
							),
						},
						...(isAbsolute
							? [
									{
										key: 'data',
										label: 'Kelengkapan Data',
										children: (
											<Fragment>
												<Form.Item
													name={fieldName(['mppCompletenessDataData', 'mppId'])}
													initialValue={
														'mppId' in props ? props.mppId : undefined
													}
													noStyle
												/>
												<Tabs
													activeKey={dataTab}
													onChange={(key) => setDataTab(key as typeof dataTab)}
													items={[
														{
															key: 'Laporan Keuangan',
															label: 'Laporan Keuangan',
														},
														{
															key: 'Laporan Penjualan',
															label: 'Laporan Penjualan',
														},
														{
															key: 'Data Rekening Koran',
															label: 'Data Rekening Koran',
														},
														{
															key: 'Dokumen Tambahan',
															label: 'Dokumen Tambahan',
														},
													]}
												/>
												<div hidden={dataTab !== 'Laporan Keuangan'}>
													<Row
														align="middle"
														style={{ marginBottom: 20 }}
													>
														<Col flex="auto" />
														<Col flex="none">
															{!isReviewing && (
																<Button
																	style={{
																		borderRadius: 50,
																		fontSize: 12,
																		height: 24,
																		fontWeight: 700,
																		...(viewOnly
																			? {}
																			: {
																					color: '#1557BC',
																					borderColor: '#1557BC',
																				}),
																	}}
																	icon={'+'}
																	onClick={async () => {
																		const newRecord =
																			await financialReportModal.open({
																				action: 'add',
																				customerId,
																				prospectId: prospectId!,
																			});

																		if (!newRecord) return;
																		form.setFieldValue(
																			fieldName([
																				'mppCompletenessDataData',
																				'financialReports',
																			]),
																			[
																				...(value?.mppCompletenessDataData
																					?.financialReports ?? []),
																				newRecord,
																			],
																		);
																	}}
																>
																	Add
																</Button>
															)}
														</Col>
													</Row>
													<Form.Item
														name={fieldName([
															'mppCompletenessDataData',
															'financialReports',
														])}
														initialValue={
															financialReports?.map((i) => {
																return {
																	id: i.id,
																	auditStatusId: i.auditStatusId,
																	auditorName: i.auditorName,
																	balanceSheet: i.balanceSheet,
																	financialReportFile: i.financialReportFile,
																	periodTypeDetailId: i.periodTypeDetailId,
																	periodTypeId: i.periodTypeId,
																	profitLoss: i.profitLoss,
																	year: i.year,
																} as SubmitMPPFinancialReport;
															}) ?? []
														}
														wrapperCol={{ span: 24 }}
													>
														<Table
															bordered
															pagination={false}
															style={{ marginBottom: 20 }}
															dataSource={
																value?.mppCompletenessDataData?.financialReports?.map(
																	(record, key) => {
																		return {
																			...record,
																			key,
																		};
																	},
																) ?? []
															}
															columns={[
																{
																	title: 'No',
																	align: 'center',
																	width: 1,
																	render: (_, __, index) => index + 1,
																},
																{
																	title: 'Action',
																	align: 'center',
																	width: 1,
																	render: (_, record) => (
																		<a
																			href="#"
																			onClick={(e) => {
																				e.preventDefault();
																				financialReportModal.open({
																					action: 'edit',
																					prospectId: prospectId!,
																					customerId,
																					record,
																				});
																			}}
																		>
																			<FileSearchOutlined />
																		</a>
																	),
																},
																{
																	title: 'Laporan Keuangan',
																	render: (_, record) =>
																		selectFinancialReportAuditStatus.options?.find(
																			(option) =>
																				option.value === record.auditStatusId,
																		)?.label ?? '-',
																},
																{
																	title: 'Nama Auditor',
																	render: (_, record) =>
																		record.auditorName ?? '-',
																},
																{
																	title: 'Periode',
																	render: (_, record) =>
																		selectFinancialReportPeriodType.options?.find(
																			(option) =>
																				option.value === record.periodTypeId,
																		)?.label ?? '-',
																},
																{
																	title: 'Detail Periode',
																	render: (_, record) =>
																		record.periodTypeDetailId
																			? getPeriodTypeDetailLabel(
																					record.periodTypeDetailId,
																				) ?? `ID: ${record.periodTypeDetailId}`
																			: '-',
																},
																{
																	title: 'Tahun',
																	render: (_, record) => record.year ?? '-',
																},
																{
																	title: 'View Dokumen',
																	align: 'center',
																	render: (_, record) => (
																		<a
																			href="#"
																			onClick={(e) => {
																				e.preventDefault();
																				if (record.financialReportFile) {
																					openPreviewModal(
																						record.financialReportFile,
																					);
																				}
																			}}
																		>
																			<b>View Dokumen</b>
																		</a>
																	),
																},
															]}
														/>
													</Form.Item>
													<Form.Item
														name={fieldName([
															'mppCompletenessDataData',
															'financialReportAnalysisResult',
														])}
														rules={[rule.required()]}
														initialValue={financialReportAnalysisResult}
														label="Hasil Analisa"
													>
														<Input.TextArea rows={3} />
													</Form.Item>

													<Button
														type="primary"
														style={{
															display: 'block',
															width: 200,
															marginLeft: 'auto',
															marginTop: 40,
														}}
														onClick={() => setDataTab('Laporan Penjualan')}
													>
														NEXT
													</Button>
												</div>

												<div hidden={dataTab !== 'Laporan Penjualan'}>
													<Form.Item
														name={fieldName([
															'mppCompletenessDataData',
															'salesReports',
															'id',
														])}
														initialValue={salesReports?.id}
													/>
													<Form.Item
														name={fieldName([
															'mppCompletenessDataData',
															'salesReports',
															'salesReport',
														])}
														label="Upload Laporan Penjualan"
														rules={[rule.required()]}
														initialValue={salesReports?.salesReport}
														getValueProps={(value) => {
															const file = value as FileData;
															return {
																fileList: file?.id
																	? ([
																			{
																				uid: file.id,
																				name: file.name,
																				thumbUrl: file.url,
																			},
																		] as UploadFile[])
																	: [],
															};
														}}
														normalize={({ file }: { file: UploadFile }) => {
															return {
																id: file.uid,
																format: file.type,
																name: file.name,
																url: file.thumbUrl,
															} as FileData;
														}}
													>
														<Upload.Dragger
															style={{ width: 200 }}
															listType="picture"
															accept=".jpg,.jpeg,.png"
															maxCount={1}
															beforeUpload={() => false}
															onRemove={() => {
																form.setFieldValue(
																	fieldName([
																		'mppCompletenessDataData',
																		'salesReports',
																		'salesReport',
																	]),
																	null,
																);
																return false;
															}}
															onChange={async ({ file }) => {
																const fileid = Date.now().toString();
																const filepath = `customer/${customerId}/${prospectId}/financial-reports`;

																try {
																	const url = await uploadMutation.mutateAsync({
																		file: file as unknown as File,
																		fileid,
																		filepath,
																	});
																	form.setFieldValue(
																		fieldName([
																			'mppCompletenessDataData',
																			'salesReports',
																			'salesReport',
																		]),
																		{
																			format: 'jpeg',
																			id: fileid,
																			name: file.name,
																			path: filepath,
																			url,
																		},
																	);
																} catch {
																	form.setFieldValue(
																		fieldName([
																			'mppCompletenessDataData',
																			'salesReports',
																			'salesReport',
																		]),
																		null,
																	);
																}
															}}
														>
															{uploadMutation.isPending ? (
																<LoadingOutlined
																	style={{
																		fontSize: 25,
																		color: '#BABABA',
																		display: 'block',
																	}}
																/>
															) : (
																<UploadOutlined
																	style={{
																		fontSize: 25,
																		color: '#BABABA',
																		display: 'block',
																	}}
																/>
															)}
														</Upload.Dragger>
													</Form.Item>
													<Divider />
													<Form.Item
														name={fieldName([
															'mppCompletenessDataData',
															'salesReportAnalysisResult',
														])}
														label="Analisa Laporan Penjualan"
														initialValue={salesReportAnalysisResult}
														rules={[rule.required()]}
													>
														<Input.TextArea rows={3} />
													</Form.Item>
													<Button
														type="primary"
														style={{
															display: 'block',
															width: 200,
															marginLeft: 'auto',
															marginTop: 40,
														}}
														onClick={() => setDataTab('Data Rekening Koran')}
													>
														NEXT
													</Button>
												</div>

												<div hidden={dataTab !== 'Data Rekening Koran'}>
													<Row
														align="middle"
														style={{ marginBottom: 20 }}
													>
														<Col flex="auto" />
														<Col flex="none">
															{!isReviewing && (
																<Button
																	style={{
																		borderRadius: 50,
																		fontSize: 12,
																		height: 24,
																		fontWeight: 700,
																		color: '#1557BC',
																		borderColor: '#1557BC',
																	}}
																	icon={'+'}
																	onClick={async () => {
																		const newRecord =
																			await bankStatementModal.open({
																				customerId,
																				prospectId: prospectId!,
																			});

																		console.log(newRecord);
																		if (newRecord) {
																			form.setFieldValue(
																				fieldName([
																					'mppCompletenessDataData',
																					'bankStatements',
																				]),
																				[
																					...(value?.mppCompletenessDataData
																						?.bankStatements ?? []),
																					newRecord,
																				],
																			);
																		}
																	}}
																>
																	Add
																</Button>
															)}
														</Col>
													</Row>
													<Form.Item
														name={fieldName([
															'mppCompletenessDataData',
															'bankStatements',
														])}
														initialValue={
															bankStatements?.map((i) => {
																// Store full bank object from API response for form compatibility
																return {
																	accountNo: i.accountNo,
																	accountType: i.accountType,
																	averageBalance: i.averageBalance,
																	bankId:
																		i.bank ??
																		(i.bankId
																			? {
																					id: i.bankId,
																					value: `Bank ID: ${i.bankId}`,
																				}
																			: undefined), // Use bank object from API or create fallback if bankId exists
																	bankStatementFile: i.bankStatementFile,
																	bankStatementPeriod: i.bankStatementPeriod,
																	id: i.id,
																	totalCredit: i.totalCredit,
																	totalDebit: i.totalDebit,
																} as SubmitMPPBankStatement;
															}) ?? []
														}
														wrapperCol={{ span: 24 }}
													>
														<Table
															bordered
															pagination={false}
															dataSource={
																value?.mppCompletenessDataData?.bankStatements?.map(
																	(record, key) => {
																		return {
																			...record,
																			key,
																		};
																	},
																) ?? []
															}
															style={{ marginBottom: 20 }}
															columns={[
																{
																	title: 'No',
																	align: 'center',
																	width: 1,
																	render: (_, __, index) => index + 1,
																},
																{
																	title: 'View Rekening Koran ',
																	align: 'center',
																	render: (_, record) => (
																		<a
																			href="#"
																			onClick={(e) => {
																				e.preventDefault();
																				if (
																					!record.bankStatementFile ||
																					!record.bankStatementFile.url
																				)
																					return;
																				openPreviewModal(
																					record.bankStatementFile,
																				);
																			}}
																		>
																			<FileSearchOutlined
																				style={{
																					color: record.bankStatementFile?.url
																						? '#1890ff'
																						: '#d9d9d9',
																				}}
																			/>
																		</a>
																	),
																},
																{
																	title: 'Jenis Rekening',
																	render: (_, record) =>
																		selectBankStatementType.options?.find(
																			(option) =>
																				option.value === record.accountType,
																		)?.label ?? '-',
																},
																{
																	title: 'Nama Bank',
																	render: (_, record) => {
																		// Handle both bank object and bank ID number for robust display
																		if (record.bankId) {
																			if (
																				typeof record.bankId === 'object' &&
																				'value' in record.bankId
																			) {
																				// Bank object from API/form data
																				return record.bankId.value;
																			} else if (
																				typeof record.bankId === 'number'
																			) {
																				// Fallback: bank ID number (shouldn't happen with current implementation)
																				return `Bank ID: ${record.bankId}`;
																			}
																		}
																		return '-';
																	},
																},
																{
																	title: 'Account Number',
																	render: (_, record) =>
																		record.accountNo ?? '-',
																},
																{
																	title: 'Periode',
																	render: (_, record) =>
																		record.bankStatementPeriod
																			? dayjs(
																					record.bankStatementPeriod,
																				).format('YYYY-MM')
																			: '-',
																},
															]}
														/>
													</Form.Item>
													<Form.Item
														label="Hasil Analisa"
														name={fieldName([
															'mppCompletenessDataData',
															'bankStatementAnalysisResult',
														])}
														initialValue={bankStatementAnalysisResult}
														rules={[rule.required()]}
													>
														<Input.TextArea rows={3} />
													</Form.Item>

													<Button
														type="primary"
														style={{
															display: 'block',
															width: 200,
															marginLeft: 'auto',
															marginTop: 40,
														}}
														onClick={() => setDataTab('Dokumen Tambahan')}
													>
														NEXT
													</Button>
												</div>

												<div hidden={dataTab !== 'Dokumen Tambahan'}>
													<Row
														align="middle"
														style={{ marginBottom: 20 }}
													>
														<Col flex="auto" />
														<Col flex="none">
															{!isReviewing && (
																<Button
																	style={{
																		borderRadius: 50,
																		fontSize: 12,
																		height: 24,
																		fontWeight: 700,
																		color: '#1557BC',
																		borderColor: '#1557BC',
																	}}
																	icon={'+'}
																	onClick={async () => {
																		const newRecord =
																			await otherDocumentModal.open({
																				customerId,
																				prospectId: prospectId!,
																			});

																		if (newRecord) {
																			form.setFieldValue(
																				fieldName([
																					'mppCompletenessDataData',
																					'otherDocuments',
																				]),
																				[
																					...(value?.mppCompletenessDataData
																						?.otherDocuments ?? []),
																					newRecord,
																				],
																			);
																		}
																	}}
																>
																	Add
																</Button>
															)}
														</Col>
													</Row>
													<Form.Item
														name={fieldName([
															'mppCompletenessDataData',
															'otherDocuments',
														])}
														initialValue={
															otherDocuments?.map((i) => {
																return {
																	id: i.id,
																	document: i.document,
																	documentName: i.documentName,
																	notes: i.notes,
																} as SubmitMPPOtherDocument;
															}) ?? []
														}
														wrapperCol={{ span: 24 }}
													/>

													<div hidden={dataTab !== 'Dokumen Tambahan'}>
														<Table
															bordered
															pagination={false}
															dataSource={
																value?.mppCompletenessDataData?.otherDocuments?.map(
																	(record, key) => {
																		return {
																			...record,
																			key,
																		};
																	},
																) ?? []
															}
															style={{ marginBottom: 20 }}
															columns={[
																{
																	title: 'No',
																	align: 'center',
																	width: 1,
																	render: (_, __, index) => index + 1,
																},
																{
																	title: 'View Dokumen',
																	align: 'center',
																	render: (_, record) => (
																		<a
																			href="#"
																			onClick={(e) => {
																				e.preventDefault();
																				if (!record.document) return;
																				openPreviewModal(record.document);
																			}}
																		>
																			<FileSearchOutlined />
																		</a>
																	),
																},
																{
																	title: 'Nama Dokumen',
																	render: (_, record) =>
																		record.documentName ?? '-',
																},
																{
																	title: 'Keterangan',
																	render: (_, record) => record.notes ?? '-',
																},
															]}
														/>
													</div>
												</div>
											</Fragment>
										),
									},
								]
							: []),
						{
							key: 'memo',
							label: 'Memo Pencairan Pembiayaan',
							children: (
								<Fragment>
									<Typography.Title level={3}>
										Latar Belakang Realisasi
									</Typography.Title>
									<Form.Item
										name={fieldName('realizationBackground')}
										wrapperCol={{ span: 24 }}
										initialValue={backgroundRealisation}
									>
										<Input.TextArea
											rows={4}
											readOnly={isReviewing || isReturn}
											disabled={isReturn || isReviewing}
										/>
									</Form.Item>
									<Divider />
									<div style={{ marginBottom: '20px', fontWeight: 'bold' }}>
										Informasi Realisasi
									</div>
									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												background: 'white',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
												marginBottom: '20px',
											}}
										>
											<thead>
												<tr
													style={{ backgroundColor: '#2D4C82', color: 'white' }}
												>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '160px',
														}}
													>
														Nama Customer
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '80px',
														}}
													>
														Fasilitas
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '190px',
														}}
													>
														No. MPP
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														NTF
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														OS Pokok Hutang
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														Angsuran/Bulan
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '100px',
														}}
													>
														Jumlah Unit
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '190px',
														}}
													>
														Detail PO Pending
													</th>
												</tr>
											</thead>
											<tbody>
												{(() => {
													// Get unique MPP numbers for existing facilities
													const existingFacilities =
														customerFacility?.existingFacilities || [];
													const uniqueExistingMppNos = Array.from(
														new Set(
															existingFacilities.map(
																(facility) => facility.mppNo || '',
															),
														),
													);

													// Get unique MPP numbers for pending facilities
													const pendingFacilities =
														customerFacility?.pendingFacilities || [];
													const uniquePendingMppNos = Array.from(
														new Set(
															pendingFacilities.map(
																(facility) => facility.mppNo || '',
															),
														),
													);

													// Always show at least one row for existing and pending
													const hasExistingRows =
														uniqueExistingMppNos.length > 0;
													const hasPendingRows = uniquePendingMppNos.length > 0;

													// If no existing facilities, add a placeholder MPP number
													if (!hasExistingRows) {
														uniqueExistingMppNos.push('');
													}

													// If no pending facilities, add a placeholder MPP number
													if (!hasPendingRows) {
														uniquePendingMppNos.push('');
													}

													return (
														<>
															{/* Existing Facilities - one row per facility */}
															{(() => {
																// Group facilities by MPP No and calculate rowspan
																const mppNoGroups = existingFacilities.reduce(
																	(groups, facility) => {
																		const mppNo = facility.mppNo || '';
																		if (!groups[mppNo]) {
																			groups[mppNo] = [];
																		}
																		groups[mppNo].push(facility);
																		return groups;
																	},
																	{} as Record<
																		string,
																		typeof existingFacilities
																	>,
																);

																return existingFacilities.map(
																	(facility, index) => {
																		const facilityNtf = facility.ntf ?? 0;
																		const facilityOutstanding =
																			facility.outstanding ?? 0;
																		const facilityInstallment =
																			facility.installment ?? 0;
																		const facilityUnitCount =
																			facility.unitCount ?? 0;
																		const facilityPoDetails =
																			facility.poNo || '';

																		const mppNo = facility.mppNo || '';
																		const facilitiesInGroup =
																			mppNoGroups[mppNo];
																		const facilityIndex =
																			facilitiesInGroup.indexOf(facility);
																		const isFirstInGroup = facilityIndex === 0;
																		const rowspan = facilitiesInGroup.length;
																		const showMppNoCell = isFirstInGroup;

																		return (
																			<tr key={`existing-${index}`}>
																				{/* Show customer name only for the first facility */}
																				{index === 0 && (
																					<>
																						<td
																							rowSpan={
																								existingFacilities.length +
																								pendingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								verticalAlign: 'top',
																							}}
																						>
																							{customerName}
																						</td>
																						<td
																							rowSpan={
																								existingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																							}}
																						>
																							Eksisting
																						</td>
																					</>
																				)}
																				{showMppNoCell && (
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							color: '#1890ff',
																						}}
																						rowSpan={rowspan}
																					>
																						{facility.mppNo || '-'}
																					</td>
																				)}
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityNtf)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityOutstanding)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityInstallment)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{facilityUnitCount}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						whiteSpace: 'nowrap',
																					}}
																				>
																					{facilityPoDetails}
																				</td>
																			</tr>
																		);
																	},
																);
															})()}

															{/* Pending Facilities - one row per facility */}
															{(() => {
																// Sort pending facilities by MPP No first to ensure proper grouping
																const sortedPendingFacilities = [
																	...pendingFacilities,
																].sort((a, b) => {
																	const aMppNo = a.mppNo || '';
																	const bMppNo = b.mppNo || '';
																	return aMppNo.localeCompare(bMppNo);
																});

																// Group facilities by MPP No and calculate rowspan
																const mppNoGroups =
																	sortedPendingFacilities.reduce(
																		(groups, facility) => {
																			const mppNo = facility.mppNo || '';
																			if (!groups[mppNo]) {
																				groups[mppNo] = [];
																			}
																			groups[mppNo].push(facility);
																			return groups;
																		},
																		{} as Record<
																			string,
																			typeof sortedPendingFacilities
																		>,
																	);

																return sortedPendingFacilities.map(
																	(facility, index) => {
																		const facilityNtf = facility.ntf ?? 0;
																		const facilityOutstanding =
																			facility.outstanding ?? 0;
																		const facilityInstallment =
																			facility.installment ?? 0;
																		const facilityUnitCount =
																			facility.unitCount ?? 0;
																		const facilityPoDetails =
																			facility.poNo || '';

																		const mppNo = facility.mppNo || '';
																		const facilitiesInGroup =
																			mppNoGroups[mppNo];
																		const facilityIndex =
																			facilitiesInGroup.indexOf(facility);
																		const isFirstInGroup = facilityIndex === 0;
																		const rowspan = facilitiesInGroup.length;
																		const showMppNoCell = isFirstInGroup;

																		return (
																			<tr key={`pending-${index}`}>
																				{/* Show customer name only if there are no existing facilities */}
																				{existingFacilities.length === 0 &&
																					index === 0 && (
																						<td
																							rowSpan={
																								existingFacilities.length +
																								sortedPendingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								verticalAlign: 'top',
																							}}
																						>
																							{customerName}
																						</td>
																					)}
																				{/* Show "Pending" label only for the first pending facility */}
																				{index === 0 && (
																					<td
																						rowSpan={
																							sortedPendingFacilities.length
																						}
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																						}}
																					>
																						Pending
																					</td>
																				)}
																				{showMppNoCell && (
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							color: '#1890ff',
																						}}
																						rowSpan={rowspan}
																					>
																						{facility.mppNo || '-'}
																					</td>
																				)}
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityNtf)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityOutstanding)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityInstallment)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{facilityUnitCount ?? 0}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						whiteSpace: 'nowrap',
																					}}
																				>
																					{facilityPoDetails}
																				</td>
																			</tr>
																		);
																	},
																);
															})()}
														</>
													);
												})()}
											</tbody>
											<tfoot>
												{(() => {
													// Determine if we should use dynamic calculations or backend data
													const isFRM = userData?.groupName === 'FRM';
													const shouldCalculateDynamically =
														isFRM && (!viewOnly || !isReviewing);

													if (shouldCalculateDynamically) {
														// FRM input mode: Use dynamic calculations
														return (
															<>
																<tr>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			fontWeight: 'bold',
																		}}
																	>
																		Pengajuan
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{submissionTotal?.ntf
																			? formatCurrency(submissionTotal.ntf)
																			: '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{submissionTotal.outstanding
																			? formatCurrency(
																					submissionTotal.outstanding,
																				)
																			: '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{submissionTotal?.installment
																			? formatCurrency(
																					submissionTotal.installment,
																				)
																			: '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{submissionTotal?.unit || '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	/>
																</tr>
																<tr
																	style={{
																		backgroundColor: '#e6f7ff',
																		fontWeight: 'bold',
																	}}
																>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	>
																		Total
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{total.ntf
																			? formatCurrency(total.ntf)
																			: '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{totalCustomerOutstanding
																			? formatCurrency(totalCustomerOutstanding)
																			: '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{totalInstallment
																			? formatCurrency(totalInstallment)
																			: '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{total.unit ?? '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	></td>
																</tr>
																<tr
																	style={{
																		backgroundColor: '#e6f7ff',
																		fontWeight: 'bold',
																	}}
																>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	>
																		Total Pengajuan Customer
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{totalCustomerSubmission
																			? formatCurrency(totalCustomerSubmission)
																			: ''}
																	</td>
																	<td
																		colSpan={4}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	/>
																</tr>
																<tr
																	style={{
																		backgroundColor: '#e6f7ff',
																		fontWeight: 'bold',
																	}}
																>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	>
																		Plafond Customer Yang Disetujui
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{totalPlafondApproved
																			? formatCurrency(totalPlafondApproved)
																			: ''}
																	</td>
																	<td
																		colSpan={4}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	/>
																</tr>
																<tr
																	style={{
																		backgroundColor: '#e6f7ff',
																		fontWeight: 'bold',
																	}}
																>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	>
																		Sisa Plafond Customer
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{totalRemainingPlafon
																			? formatCurrency(totalRemainingPlafon)
																			: ''}
																	</td>
																	<td
																		colSpan={4}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	/>
																</tr>
															</>
														);
													} else {
														// View-only/Review mode: Use backend data directly

														const requestedFacilities =
															customerFacility?.requestedFacilities[0];

														return (
															<>
																<tr>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			fontWeight: 'bold',
																		}}
																	>
																		Pengajuan
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			requestedFacilities?.ntf || 0,
																		)}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			requestedFacilities?.outstanding || 0,
																		)}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			requestedFacilities?.installment || 0,
																		)}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{requestedFacilities?.unitCount || '0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	/>
																</tr>
																<tr
																	style={{
																		backgroundColor: '#e6f7ff',
																		fontWeight: 'bold',
																	}}
																>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	>
																		Total
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			customerFacilitySummary?.totalNtf || 0,
																		)}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			customerFacilitySummary?.totalOutstanding ||
																				0,
																		)}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			customerFacilitySummary?.installmentAmount ||
																				0,
																		)}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{customerFacilitySummary?.totalUnitCount ||
																			'0'}
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	></td>
																</tr>
																<tr
																	style={{
																		backgroundColor: '#e6f7ff',
																		fontWeight: 'bold',
																	}}
																>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	>
																		Total Pengajuan Customer
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			customerFacilitySummary?.totalCustomerApplication ||
																				0,
																		)}
																	</td>
																	<td
																		colSpan={4}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	/>
																</tr>
																<tr
																	style={{
																		backgroundColor: '#e6f7ff',
																		fontWeight: 'bold',
																	}}
																>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	>
																		Plafond Customer Yang Disetujui
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			customerFacilitySummary?.totalPlafon || 0,
																		)}
																	</td>
																	<td
																		colSpan={4}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	/>
																</tr>
																<tr
																	style={{
																		backgroundColor: '#e6f7ff',
																		fontWeight: 'bold',
																	}}
																>
																	<td
																		colSpan={3}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																		}}
																	>
																		Sisa Plafond Customer
																	</td>
																	<td
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	>
																		{formatCurrency(
																			customerFacilitySummary?.totalRemainingPlafon ||
																				0,
																		)}
																	</td>
																	<td
																		colSpan={4}
																		style={{
																			padding: '10px',
																			border: '1px solid #ccc',
																			textAlign: 'right',
																		}}
																	/>
																</tr>
															</>
														);
													}
												})()}
											</tfoot>
										</table>
									</div>

									<div
										style={{
											marginBottom: '20px',
											marginTop: '40px',
											fontWeight: 'bold',
										}}
									>
										A/n Afiliasi Customer (jika ada)
									</div>

									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												background: 'white',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
												marginBottom: '20px',
											}}
										>
											<thead>
												<tr
													style={{ backgroundColor: '#2D4C82', color: 'white' }}
												>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '160px',
														}}
													>
														Nama Customer
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '80px',
														}}
													>
														Fasilitas
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '190px',
														}}
													>
														No. MPP
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														NTF
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														OS Pokok Hutang
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '130px',
														}}
													>
														Angsuran/Bulan
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '100px',
														}}
													>
														Jumlah Unit
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
															width: '190px',
														}}
													>
														Detail PO Pending
													</th>
												</tr>
											</thead>
											<tbody>
												{customerAffiliatesFacility?.map(
													(
														{
															customerAffiliateFacilities,
															customerAffiliateName,
														},
														affiliateIndex,
													) => {
														// Collect existing facilities with metadata
														const existingFacilities =
															customerAffiliateFacilities.flatMap(
																(facilityCollection, collectionIndex) => {
																	return (
																		facilityCollection.existingFacilities || []
																	).map((facility, facilityIndex) => ({
																		...facility,
																		type: 'existing' as const,
																		collectionIndex,
																		uniqueId: `${affiliateIndex}-${collectionIndex}-existing-${facilityIndex}`,
																	}));
																},
															);

														// Collect pending facilities with metadata
														const unsortedPendingFacilities =
															customerAffiliateFacilities.flatMap(
																(facilityCollection, collectionIndex) => {
																	return (
																		facilityCollection.pendingFacilities || []
																	).map((facility, facilityIndex) => ({
																		...facility,
																		type: 'pending' as const,
																		collectionIndex,
																		uniqueId: `${affiliateIndex}-${collectionIndex}-pending-${facilityIndex}`,
																	}));
																},
															);

														// Sort pending facilities by MPP No first to ensure proper grouping
														let pendingFacilities = [
															...unsortedPendingFacilities,
														].sort((a, b) => {
															const aMppNo = a.mppNo || '';
															const bMppNo = b.mppNo || '';
															return aMppNo.localeCompare(bMppNo);
														});

														// If no pending facilities, create a default empty row
														if (pendingFacilities.length === 0) {
															pendingFacilities = [
																{
																	mppNo: '',
																	ntf: 0,
																	outstanding: 0,
																	installment: 0,
																	unitCount: 0,
																	poId: 0,
																	poNo: '',
																	type: 'pending' as const,
																	collectionIndex: 0,
																	uniqueId: `${affiliateIndex}-0-pending-default`,
																},
															];
														}

														// Group pending facilities by MPP No to ensure proper ordering
														const pendingByMppNo = pendingFacilities.reduce(
															(groups, facility) => {
																const mppNo = facility.mppNo || '';
																if (!groups[mppNo]) {
																	groups[mppNo] = [];
																}
																groups[mppNo].push(facility);
																return groups;
															},
															{} as Record<string, typeof pendingFacilities>,
														);

														// Sort pending facilities: group by MPP No
														const sortedPendingMppNos =
															Object.keys(pendingByMppNo).sort();

														// Calculate totals
														const totalPendingFacilities =
															pendingFacilities.length;
														const totalExistingFacilities =
															existingFacilities.length;
														const hasExistingFacilities =
															totalExistingFacilities > 0;

														return (
															<>
																{/* Render existing facilities first */}
																{existingFacilities.map((facility, index) => {
																	const facilityNtf = facility.ntf ?? 0;
																	const facilityOutstanding =
																		facility.outstanding ?? 0;
																	const facilityInstallment =
																		facility.installment ?? 0;
																	const facilityUnitCount =
																		facility.unitCount ?? 0;
																	const facilityPoDetails = facility.poNo || '';

																	const isFirstFacility = index === 0;
																	const mppNo = facility.mppNo || '';

																	return (
																		<tr key={facility.uniqueId}>
																			{/* Show customer name only for the first facility */}
																			{isFirstFacility && (
																				<td
																					rowSpan={
																						existingFacilities.length +
																						pendingFacilities.length
																					}
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						verticalAlign: 'top',
																					}}
																				>
																					{customerAffiliateName}
																				</td>
																			)}
																			{/* Show "Eksisting" label only for the first existing facility */}
																			{isFirstFacility && (
																				<td
																					rowSpan={existingFacilities.length}
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																					}}
																				>
																					Eksisting
																				</td>
																			)}
																			{/* Show MPP No */}
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					color: '#1890ff',
																				}}
																			>
																				{mppNo || '-'}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityNtf)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityOutstanding)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityInstallment)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{facilityUnitCount ?? 0}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					whiteSpace: 'nowrap',
																				}}
																			>
																				{facilityPoDetails}
																			</td>
																		</tr>
																	);
																})}

																{/* Render pending facilities grouped by MPP No */}
																{sortedPendingMppNos.map((mppNo, mppIndex) => {
																	const facilitiesInGroup =
																		pendingByMppNo[mppNo];

																	return (
																		<>
																			{/* Pending Facilities in this MPP group */}
																			{facilitiesInGroup.map(
																				(facility, index) => {
																					const facilityNtf = facility.ntf ?? 0;
																					const facilityOutstanding =
																						facility.outstanding ?? 0;
																					const facilityInstallment =
																						facility.installment ?? 0;
																					const facilityUnitCount =
																						facility.unitCount ?? 0;
																					const facilityPoDetails =
																						facility.poNo || '';

																					const isFirstInGroup = index === 0;
																					const isFirstPendingOverall =
																						mppIndex === 0 && index === 0;
																					const showCustomerName =
																						isFirstPendingOverall &&
																						!hasExistingFacilities;

																					return (
																						<tr key={facility.uniqueId}>
																							{/* Show customer name only if no existing facilities and this is the first pending facility */}
																							{showCustomerName && (
																								<td
																									rowSpan={
																										existingFacilities.length +
																										pendingFacilities.length
																									}
																									style={{
																										padding: '10px',
																										border: '1px solid #ccc',
																										verticalAlign: 'top',
																									}}
																								>
																									{customerAffiliateName}
																								</td>
																							)}
																							{/* Show "Pending" label only for the first pending facility overall */}
																							{isFirstPendingOverall && (
																								<td
																									rowSpan={
																										totalPendingFacilities
																									}
																									style={{
																										padding: '10px',
																										border: '1px solid #ccc',
																									}}
																								>
																									Pending
																								</td>
																							)}
																							{/* Show MPP No only for the first facility in this MPP group */}
																							{isFirstInGroup && (
																								<td
																									style={{
																										padding: '10px',
																										border: '1px solid #ccc',
																										color: '#1890ff',
																									}}
																									rowSpan={
																										facilitiesInGroup.length
																									}
																								>
																									{facility.mppNo || '-'}
																								</td>
																							)}
																							<td
																								style={{
																									padding: '10px',
																									border: '1px solid #ccc',
																									textAlign: 'right',
																								}}
																							>
																								{formatCurrency(facilityNtf)}
																							</td>
																							<td
																								style={{
																									padding: '10px',
																									border: '1px solid #ccc',
																									textAlign: 'right',
																								}}
																							>
																								{formatCurrency(
																									facilityOutstanding,
																								)}
																							</td>
																							<td
																								style={{
																									padding: '10px',
																									border: '1px solid #ccc',
																									textAlign: 'right',
																								}}
																							>
																								{formatCurrency(
																									facilityInstallment,
																								)}
																							</td>
																							<td
																								style={{
																									padding: '10px',
																									border: '1px solid #ccc',
																									textAlign: 'right',
																								}}
																							>
																								{facilityUnitCount ?? 0}
																							</td>
																							<td
																								style={{
																									padding: '10px',
																									border: '1px solid #ccc',
																									whiteSpace: 'nowrap',
																								}}
																							>
																								{facilityPoDetails}
																							</td>
																						</tr>
																					);
																				},
																			)}
																		</>
																	);
																})}
															</>
														);
													},
												)}
											</tbody>
											<tfoot>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Total Afiliasi Customer
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.totalNtf ?? 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.totalOutstanding ??
																0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.installmentAmount ??
																0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.totalUnitCount ||
																0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													></td>
												</tr>
											</tfoot>
										</table>
									</div>
									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
											}}
										>
											<tbody>
												<tr style={{ backgroundColor: '#f0f0f0' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
															width: '320px',
														}}
													>
														GRAND TOTAL PENGAJUAN (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'left',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(
															obligorDetail?.grandTotalApplication,
														)}
													</td>
												</tr>
												<tr style={{ backgroundColor: '#f0f0f0' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														PLAFOND YANG DISETUJUI (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'left',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(obligorDetail?.grandTotalPlafon)}
													</td>
												</tr>
												<tr style={{ backgroundColor: '#fffbe6' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														SISA PLAFOND (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'left',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(
															obligorDetail?.grandTotalRemainingPlafon,
														)}
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</Fragment>
							),
						},
					]}
				/>

				<Form.Item
					name={fieldName('agingDate')}
					label="Aging Date"
					getValueProps={(value) => ({
						value: value && dayjs(value),
					})}
					normalize={(value) => value && dayjs(value).utc(true).toISOString()}
					rules={isReviewing || groupName === 'CCA' ? [] : [rule.required()]}
					initialValue={agingDate}
				>
					<DatePicker
						style={{ width: 200 }}
						disabled={
							isReviewing || groupName === 'CCA' || viewOnly || isReturn
						}
					/>
				</Form.Item>
				<Divider />
				<Row
					align="middle"
					style={{ marginBottom: 20 }}
				>
					<Col flex="auto">
						<strong>Ringkasan Rincian Detail Transaksi</strong>
					</Col>
					<Col flex="none">
						{!isReviewing && (
							<Button
								style={{
									borderRadius: 50,
									fontSize: 12,
									height: 24,
									fontWeight: 700,
								}}
								icon={'+'}
								disabled={viewOnly || isReturn}
								onClick={async () => {
									let simulationId: number | undefined;
									let simulationNo: string = '';

									if ('spfId' in props) {
										// Use simulation modal when spfId is available
										const simulationResult = await simulationModal.open({
											spfId: props.spfId,
											disabledIds:
												value?.transactionDetailSummary?.map(
													(i) => i.creditSimulationId,
												) ?? [],
										});

										if (simulationResult) {
											simulationId = simulationResult.simulationId;
											simulationNo = simulationResult.simulationNo;
										}
									} else if ('mppId' in props) {
										// Use simulation options from MPP detail when only mppId is available
										const simulationOptions =
											mppDetailQuery.data?.data?.simulationOptions ?? [];
										const disabledIds =
											value?.transactionDetailSummary?.map(
												(i) => i.creditSimulationId,
											) ?? [];
										const availableSimulations = simulationOptions.filter(
											(sim) => !disabledIds.includes(sim.simulationId),
										);

										if (availableSimulations.length === 0) {
											await modal.confirm({
												title:
													'Tidak ada simulasi yang tersedia untuk ditambahkan.',
												cancelText: 'OK',
												okButtonProps: { style: { display: 'none' } },
											});
											return;
										}

										if (availableSimulations.length === 1) {
											// Auto-select if only one simulation is available
											const selectedSimulation = availableSimulations[0];
											simulationId = selectedSimulation.simulationId;
											simulationNo = selectedSimulation.simulationNo;
										} else {
											// Show selection modal for multiple simulations using confirm with subtitle
											const userConfirmed = await modal.confirm({
												title: 'Pilih Simulasi',
												subTitle:
													'Silakan lihat daftar simulasi yang tersedia. Fitur pemilihan interaktif akan segera diperbaiki.',
												okText: 'GUNAKAN SIMULASI PERTAMA',
												cancelText: 'BATAL',
											});

											// Show available simulations in console for debugging
											console.log(
												'Available simulations:',
												availableSimulations,
											);

											if (userConfirmed) {
												// For now, auto-select the first available simulation
												// TODO: Implement proper interactive selection
												const selectedSimulation = availableSimulations[0];
												simulationId = selectedSimulation.simulationId;
												simulationNo = selectedSimulation.simulationNo;
											}
										}
									}
									if (!simulationId) return;

									const res = await simulationDetail.mutateAsync(simulationId);
									transactionForm.form.resetFields();

									setView({
										type: 'new-transaction',
										data: {
											creditSimulationId: simulationId,
											simulationId,
											assetType: res.data.financingObject,
											brand: res.data.brand,
											insuranceAmount: res.data.insuranceAmount,
											insuranceType: res.data.insuranceType,
											segmentIndustry: res.data.segmentIndustry,
											financingProduct: res.data.financingProduct,
											productId: res.data.productId,
											totalDownPayment: Number(res.data.totalDownPayment) || 0,
											totalInstallment: Number(res.data.totalInstallment) || 0,
											totalNtf: Number(res.data.totalNtf) || 0,
											totalUnit: Number(res.data.unitCount) || 0,
											// Add calculation status for UI feedback
											calculationPerformed: true,
											creditSimulationNo: simulationNo,
										},
									});
								}}
							>
								Add
							</Button>
						)}
					</Col>
				</Row>
				<Form.Item
					name={fieldName('transactionDetailSummary')}
					rules={[
						{
							validator: (_, value) => {
								if (!value || value.length === 0) {
									return Promise.reject('Minimal satu transaksi diperlukan');
								}

								const missingSPK = value.some(
									(transaction: TransactionForm) => !transaction.spkFile,
								);
								if (missingSPK) {
									return Promise.reject(
										'Dokumen SPK wajib diisi untuk semua transaksi',
									);
								}

								return Promise.resolve();
							},
						},
					]}
					wrapperCol={{ span: 24 }}
					initialValue={transactionDetailSummary?.map((record) => {
						return {
							simulationId: record.creditSimulationId,
							creditSimulationId: record.creditSimulationId,
							kkbRewardAvailableId: record.kkbRewardAvailableId,
							kkbRewardPercentage: record.kkbRewardPercentage,
							kkbReferralName: record.kkbReferralName,
							bankId: record.bankId,
							bankAccountNumber: record.bankAccountNumber,
							bankBranch: record.bankBranch,
							bank: record.bank,
							wayOfPayment:
								typeof record.wayOfPayment === 'string'
									? {
											id:
												'wayOfPaymentId' in record
													? (record.wayOfPaymentId as number)
													: 0,
											value: record.wayOfPayment,
										}
									: record.wayOfPayment,
							wayOfPaymentNotes: record.wayOfPaymentNotes,
							financialPurposeId: record.financialPurposeId,
							assetInformations:
								record.assetInformations?.map((i) => {
									return {
										bpkbOwnerName: i.bpkbOwnerName,
										assetUser: i.assetUser,
										bowheerId: i.bouwheer?.id ? i.bouwheer.id : '',
										unitAmount: i.unitAmount,
										postalCodeId: i.address.postalId,
										// for ui only
										address: i.address,
										bowheer: i.bouwheer,
									};
								}) ?? [],
							// for ui only
							assetType: record.assetType,
							productId: record.productId,
							brand: record.merk,
							totalUnit: record.unitCount,
							totalNtf: record.totalNtf,
							totalInstallment: record.totalInstallment,
							totalDownPayment: record.totalDownPayment,
							insuranceType: record.insuranceType,
							insuranceAmount: record.insuranceAmount,
							hasDealerRefund: record.hasDealerRefund,
							dealerRefundAmount: record.dealerRefundAmount,
							spkFile: record.spkFile,
							segmentIndustry: record.segmentIndustry,
							financingProduct: record.financingProduct,
							creditSimulationNo: record.creditSimulationNo,
						} as TransactionForm;
					})}
				>
					<Table
						bordered
						pagination={false}
						scroll={{ x: 'max-content' }}
						dataSource={
							value?.transactionDetailSummary?.map((d) => {
								return {
									...d,
									key: d.creditSimulationId,
								} as unknown as TransactionForm;
							}) ?? []
						}
						columns={[
							{
								title: 'Edit',
								hidden: isReviewing || isReturn || viewOnly,
								align: 'center',
								width: 1,
								fixed: 'left',
								render: (_, data, index) => {
									return (
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault();
												transactionForm.form.resetFields();
												transactionForm.form.setFieldsValue(data);

												setView({
													type: 'edit-transaction',
													data,
													index,
												});
											}}
											style={
												viewOnly
													? { cursor: 'not-allowed', color: '#bababa' }
													: {}
											}
										>
											<EditOutlined />
										</a>
									);
								},
							},
							{
								title: 'No. Simulasi',
								render: (_, record) => (
									<a
										onClick={() => {
											navigate('SimulationPage', {
												simulationId: record.simulationId,
												showResult: true,
												viewOnly: true,

												onBack: () =>
													navigate(
														'MPPPage',
														'mppId' in props
															? { mppId: props.mppId }
															: { spfId: props.spfId },
													),
											});
										}}
									>
										{record.creditSimulationNo ?? '-'}
									</a>
								),
								// record.creditSimulationNo,
							},
							{
								title: 'View Detail',
								hidden: !isReviewing,
								align: 'center',
								fixed: 'left',
								render: (_, data) => {
									return (
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault();
												setView({
													type: 'view-transaction',
													data,
												});
											}}
										>
											<FileSearchOutlined />
										</a>
									);
								},
							},
							{
								title: 'Jenis Barang',
								render: (_, record) => record.assetType ?? '-',
							},
							{
								title: 'Product ID',
								render: (_, record) => record.productId ?? '-',
							},
							{
								title: 'Merk Kendaraan',
								render: (_, record) => record.brand ?? '-',
							},
							{
								title: 'Jumlah Unit',
								render: (_, record) => {
									const unitAmount =
										record.assetInformations?.reduce(
											(acc, curr) => acc + curr.unitAmount,
											0,
										) ?? 0;
									return unitAmount || '-';
								},
							},
							{
								title: 'Total NTF',
								render: (_, record) => {
									const unitAmount =
										record.assetInformations?.reduce(
											(acc, curr) => acc + curr.unitAmount,
											0,
										) ?? 0;
									// const hasCalculation = (record as TransactionData)
									// 	?.calculationPerformed;
									return (
										<div
											style={{ display: 'flex', alignItems: 'center', gap: 8 }}
										>
											<span>
												{record.totalNtf
													? formatCurrency(record.totalNtf * unitAmount)
													: '-'}
											</span>
										</div>
									);
								},
							},
							{
								title: 'Total Angsuran/bulan',
								render: (_, record) => {
									const unitAmount =
										record.assetInformations?.reduce(
											(acc, curr) => acc + curr.unitAmount,
											0,
										) ?? 0;
									return record.totalInstallment
										? formatCurrency(record.totalInstallment * unitAmount)
										: '-';
								},
							},
							{
								title: 'Total Down Payment',
								render: (_, record) => {
									const unitAmount =
										record.assetInformations?.reduce(
											(acc, curr) => acc + curr.unitAmount,
											0,
										) ?? 0;
									return record.totalDownPayment
										? formatCurrency(record.totalDownPayment * unitAmount)
										: '-';
								},
							},
							{
								title: 'Tipe Asuransi',
								render: (_, record) => record.insuranceType ?? '-',
							},
							{
								title: 'Dokumen SPK',
								fixed: 'right',
								hidden: isReturn || viewOnly || isReviewing,
								align: 'center',
								colSpan: isReturn || viewOnly ? 1 : 2,
								render: (_, __, index) => {
									const isFRM = userData?.groupName === 'FRM';
									const hasFile =
										value?.transactionDetailSummary?.[index]?.spkFile;
									const canUpload = isFRM;

									return (
										<Button
											type="link"
											icon={canUpload ? <UploadOutlined /> : <EyeOutlined />}
											onClick={async () => {
												const currentFile =
													value?.transactionDetailSummary?.[index]?.spkFile;
												if (canUpload) {
													const uploadedFile = await spkDocumentModal.open({
														customerId: customerId!,
														prospectId: prospectId!,
														transactionIndex: index,
														currentFile: currentFile as FileData | undefined,
													});

													if (uploadedFile) {
														const summary =
															value?.transactionDetailSummary?.[index];
														if (summary) {
															form.setFieldValue(
																fieldName(['transactionDetailSummary', index]),
																{
																	...summary,
																	spkFile: uploadedFile,
																} as typeof summary,
															);
														}
													}
												} else {
													// For non-FRM users, open modal in view-only mode
													await spkDocumentModal.open({
														customerId: customerId!,
														prospectId: prospectId!,
														transactionIndex: index,
														currentFile: currentFile as FileData | undefined,
													});
												}
											}}
											style={{
												color: hasFile
													? canUpload
														? '#52c41a'
														: '#1890ff'
													: '#1677ff',
											}}
										>
											{canUpload
												? hasFile
													? 'Replace'
													: 'Upload'
												: hasFile
													? 'View'
													: 'No Document'}
										</Button>
									);
								},
							},
							{
								colSpan: isReturn || viewOnly || isReviewing ? 1 : 0,
								title:
									isReturn || viewOnly || isReviewing
										? 'Dokumen SPK'
										: undefined,
								fixed: 'right',
								align: 'center',
								render: (_, record) => {
									if (!record.spkFile) {
										return <FileSearchOutlined style={{ color: '#bababa' }} />;
									}

									return (
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault();
												openPreviewModal(record.spkFile);
											}}
										>
											<FileSearchOutlined />
										</a>
									);
								},
							},
						]}
					/>
				</Form.Item>

				{(isReviewing || groupName === 'CCA') && (
					<Fragment>
						<div style={{ marginBottom: 20 }}>
							<FleetTitle>Approval History</FleetTitle>
						</div>
						<Table
							bordered
							pagination={false}
							dataSource={approvalHistory?.map((history, index) => ({
								...history,
								key: index,
							}))}
							columns={[
								{
									title: 'No',
									align: 'center',
									render: (_, __, index) => index + 1,
								},
								{
									title: 'Name',
									render: (_, record) => record.name,
								},
								{
									title: 'Date',
									align: 'center',
									render: (_, record) =>
										record.date && dayjs(record.date).format('DD/MM/YYYY'),
								},
								{
									title: 'Process',
									align: 'center',
									render: (_, record) => record.process,
								},
								{
									title: 'Status',
									align: 'center',
									render: (_, record) => record.status,
								},
								{
									title: 'Approval Notes',
									align: 'center',
									render: (_, record) => record.notes,
								},
							]}
						/>
						<Divider />
						<Form.Item
							name={fieldName('action')}
							label="Approval"
							rules={[rule.required()]}
						>
							<Select
								style={{ width: 200 }}
								options={approvalActionOptions?.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
							/>
						</Form.Item>
					</Fragment>
				)}

				<Divider />

				<Form.Item
					name={fieldName('notes')}
					label={
						isReviewing
							? 'Approval Notes'
							: groupName === 'CCA'
								? 'Verification Notes'
								: 'Notes'
					}
					rules={[rule.required()]}
				>
					<Input.TextArea rows={4} />
				</Form.Item>

				<Button
					type="primary"
					htmlType="submit"
					style={{
						display: 'block',
						width: 200,
						marginLeft: 'auto',
						marginTop: 40,
					}}
					loading={
						submitMPPMutation.isPending || submitMPPFCOMMutation.isPending
					}
					disabled={
						submitMPPMutation.isPending ||
						submitMPPFCOMMutation.isPending ||
						viewOnly
					}
				>
					SUBMIT
				</Button>
			</Form>
		);
	};

	const renderTransactionForm = () => {
		const data =
			view.type === 'new-transaction' ||
			view.type === 'edit-transaction' ||
			view.type === 'view-transaction'
				? view.data
				: undefined;

		if (!data) return null;

		const { form, fieldName, value } = transactionForm;

		const totalUnit = value?.assetInformations?.reduce(
			(acc, curr) => acc + curr.unitAmount,
			0,
		);

		const { totalNtf = 0 } = data ?? {};

		// Calculate KKB reward based on totalNtf and percentage
		// Formula: (totalNtf * kkbRewardPercentage / 100) / totalUnit for per unit
		const kkbRewardPercentage = value?.kkbRewardPercentage ?? 0;
		const totalKkbReward = (totalNtf * kkbRewardPercentage) / 100;

		const kkbRewardPerUnit =
			(totalUnit ?? 0) > 0 ? totalKkbReward / (totalUnit ?? 1) : 0;
		const kkbRewardAllUnit = totalKkbReward;

		// Define which order sources should have rewards disabled
		const getDisabledRewardOrderSources = () => {
			return [
				{ id: 3, value: 'Captive JF Commercial' },
				// { id: 4, value: 'Captive JF Corporate' },
				// Note: Captive Non-JF (id: 2) should have rewards enabled
				// Note: Captive MTF (id: 5) should have rewards enabled
			];
		};

		// Check if current order source should have rewards disabled
		const shouldDisableReward = () => {
			if (!orderSource) return false;
			const disabledSources = getDisabledRewardOrderSources();
			return disabledSources.some(
				(source) =>
					source.id === orderSource.id || source.value === orderSource.value,
			);
		};

		const disableReward = shouldDisableReward();

		// Debug: Log orderSource information to help troubleshoot
		// console.log('OrderSource Debug:', {
		// 	id: orderSource?.id,
		// 	value: orderSource?.value,
		// 	disableReward,
		// 	disabledSources: getDisabledRewardOrderSources(),
		// });

		// console.log(form.getFieldsValue());

		return (
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={view.type === 'view-transaction' ? data : undefined}
				disabled={view.type === 'view-transaction'}
				labelCol={{ span: 10 }}
				wrapperCol={{ span: 14, style: { alignSelf: 'center' } }}
				onFinish={async (values) => {
					const isConfirmed = await modal.confirm({
						title:
							'Apakah rincian detail transaksi yang Anda inputkan telah sesuai?',
						okText: 'YA',
						cancelText: 'TIDAK',
					});

					if (!isConfirmed) return;

					if (view.type === 'edit-transaction') {
						mainForm.form.setFieldValue(
							mainForm.fieldName('transactionDetailSummary'),
							(mainForm.value?.transactionDetailSummary ?? []).map(
								(record, recordIndex) => {
									return view.index === recordIndex ? values : record;
								},
							),
						);
					} else {
						const updated = [
							...(mainForm.value?.transactionDetailSummary ?? []),
							{
								...values,
								...data,
							},
						];

						mainForm.form.setFieldValue(
							mainForm.fieldName('transactionDetailSummary'),
							updated,
						);

						transactionForm.form.resetFields();
					}
					setTimeout(() => {
						setView({ type: 'main' });
					}, 500);
				}}
			>
				<Form.Item
					name={fieldName('simulationId')}
					initialValue={
						view.type === 'new-transaction' || view.type === 'edit-transaction'
							? view.data?.simulationId
							: undefined
					}
					noStyle
				/>
				<Form.Item
					name={fieldName('creditSimulationId')}
					initialValue={
						view.type === 'new-transaction' || view.type === 'edit-transaction'
							? view.data?.creditSimulationId
							: undefined
					}
					noStyle
				/>
				<Form.Item
					name={fieldName('creditSimulationNo')}
					initialValue={
						view.type === 'new-transaction' || view.type === 'edit-transaction'
							? view.data?.creditSimulationNo
							: undefined
					}
					noStyle
				/>
				{/* for ui only */}
				<Form.Item
					name={fieldName('assetType')}
					noStyle
				/>
				<Form.Item
					name={fieldName('productId')}
					noStyle
				/>
				<Form.Item
					name={fieldName('brand')}
					noStyle
				/>
				<Form.Item
					name={fieldName('totalUnit')}
					noStyle
				/>
				<Form.Item
					name={fieldName('totalNtf')}
					noStyle
				/>
				<Form.Item
					name={fieldName('totalDownPayment')}
					noStyle
				/>
				<Form.Item
					name={fieldName('totalInstallment')}
					noStyle
				/>
				<Form.Item
					name={fieldName('totalInstallment')}
					noStyle
				/>
				<Form.Item
					name={fieldName('insuranceType')}
					noStyle
				/>
				<Form.Item
					name={fieldName('insuranceAmount')}
					noStyle
				/>
				<Form.Item
					name={fieldName('spkFile')}
					noStyle
				/>
				<Form.Item
					name={fieldName('kkbRewardAvailableId')}
					noStyle
				/>
				<Form.Item
					name={fieldName('kkbReferralName')}
					noStyle
				/>
				<Tabs
					activeKey={'1'}
					items={[
						{
							key: '1',
							label: 'Rincian Detail Transaksi',
							children: (
								<Fragment>
									<div style={{ marginBottom: 20 }}>
										<Row gutter={40}>
											<Col span={12}>
												<Form.Item label="Jenis Pembiayaan">
													{data?.financingProduct?.value ?? '-'}
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item label="Jenis Asset">
													{data.assetType ?? '-'}
												</Form.Item>
											</Col>
										</Row>
										<Divider />
										<Row gutter={40}>
											<Col span={12}>
												<Form.Item label="Segment Industri">
													{data.segmentIndustry ?? '-'}
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													name={fieldName('financialPurposeId')}
													label="Tujuan Pembiayaan"
													rules={[rule.required()]}
												>
													<Select
														options={financingPurpose.map((option) => {
															return {
																label: option.value,
																value: option.id,
															};
														})}
													/>
												</Form.Item>
											</Col>
										</Row>
									</div>
								</Fragment>
							),
						},
					]}
				/>
				<Collapse
					defaultActiveKey={disableReward ? [0, 2] : [0, 1, 2]}
					items={[
						{
							label: 'Informasi Aset',
							children: (
								<Fragment>
									<Row
										align="middle"
										style={{ marginBottom: 20, gap: 40 }}
									>
										<Col flex="none">
											<strong>Total Jumlah Unit</strong>
										</Col>
										<Col flex="auto">
											<span>{totalUnit} Unit</span>
										</Col>
										{view.type !== 'view-transaction' && (
											<Col flex="none">
												<Button
													style={{
														borderRadius: 50,
														fontSize: 12,
														height: 24,
														fontWeight: 700,
														color: '#1557BC',
														borderColor: '#1557BC',
													}}
													icon={'+'}
													disabled={
														(value?.assetInformations?.length ?? 0) >=
														(data?.totalUnit ?? 0)
													}
													onClick={async () => {
														const newRecord = await assetModal.open();
														if (!newRecord) return;

														form.setFieldValue(fieldName('assetInformations'), [
															...(value?.assetInformations ?? []),
															newRecord,
														]);
													}}
												>
													Add
												</Button>
											</Col>
										)}
									</Row>
									<Form.Item
										wrapperCol={{ span: 24 }}
										name={fieldName('assetInformations')}
										initialValue={value?.assetInformations ?? []}
										rules={
											view.type === 'edit-transaction' ||
											view.type === 'new-transaction'
												? [
														{
															validator: (
																_,
																value: SubmitMPPAssetInformation[],
															) => {
																const items = value ?? [];
																const totalAssetUnit = items.reduce(
																	(acc, curr) => acc + curr.unitAmount,
																	0,
																);

																if (totalAssetUnit > data.totalUnit) {
																	return Promise.reject(
																		`Total Jumlah Unit tidak boleh lebih dari ${data.totalUnit}`,
																	);
																}
																return Promise.resolve();
															},
														},
													]
												: []
										}
									>
										<Table
											bordered
											pagination={false}
											scroll={{ x: 'max-content' }}
											dataSource={value?.assetInformations ?? []}
											columns={[
												{
													title: 'No',
													align: 'center',
													width: 1,
													render: (_, __, index) => index + 1,
												},
												{
													hidden: view.type === 'view-transaction',
													title: 'Action',
													align: 'center',
													colSpan: 2,
													render: (_, record, index) => {
														return (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	const updatedRecord = await assetModal.open({
																		action: 'edit',
																		record,
																	});

																	if (!updatedRecord) return;
																	form.setFieldValue(
																		fieldName('assetInformations'),
																		value?.assetInformations?.map(
																			(asset, assetIndex) => {
																				return index === assetIndex
																					? updatedRecord
																					: asset;
																			},
																		),
																	);
																}}
															>
																<EditOutlined />
															</a>
														);
													},
												},
												{
													hidden: view.type === 'view-transaction',
													colSpan: 0,
													align: 'center',
													render: (_, __, index) => {
														return (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();

																	form.setFieldValue(
																		fieldName('assetInformations'),
																		value?.assetInformations?.filter(
																			(_, assetIndex) => {
																				return index !== assetIndex;
																			},
																		),
																	);
																}}
															>
																<DeleteOutlined />
															</a>
														);
													},
												},
												{
													title: 'Nama STNK/BPKB/Invoice',
													render: (_, record) => record.bpkbOwnerName ?? '-',
												},
												{
													title: 'Jumlah Unit',
													render: (_, record) => record.unitAmount ?? '-',
												},
												{
													title: 'Pengguna Aset',
													render: (_, record) => record.assetUser ?? '-',
												},
												{
													title: 'Nama Bouwheer',
													render: (_, record) => record.bowheer?.name ?? '-',
												},
												{
													title: 'Bouwheer Type',
													render: (_, record) => record.bowheer?.type ?? '-',
												},
												{
													title: 'Industri Bouwheer',
													render: (_, record) =>
														record.bowheer?.industrySector?.sectorLevel5 ?? '-',
												},
												{
													title: 'Kota',
													render: (_, record) =>
														record.address?.cityName ?? '-',
												},
											]}
										/>
									</Form.Item>
								</Fragment>
							),
						},
						{
							label: 'Reward',
							collapsible: disableReward ? 'disabled' : undefined,
							children: !disableReward && (
								<Fragment>
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												name={fieldName('kkbRewardAvailableId')}
												label="Apakah ada reward KKB?"
												rules={[rule.required()]}
											>
												<Select
													disabled={viewOnly}
													options={[
														{
															label: 'Ya',
															value: 1,
														},
														{
															label: 'Tidak',
															value: 2,
														},
													]}
													placeholder="Pilih ya/tidak"
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName('kkbReferralName')}
												label="Nama Pemberi Referral"
												rules={[
													{
														validator: (_, value) => {
															const hasReward = form.getFieldValue(
																fieldName('kkbRewardAvailableId'),
															);
															if (hasReward === 2) {
																// No reward, no validation needed
																return Promise.resolve();
															}
															if (
																hasReward === 1 &&
																(!value || value.trim() === '')
															) {
																return Promise.reject(
																	new Error(
																		'Nama pemberi referral harus diisi',
																	),
																);
															}
															return Promise.resolve();
														},
													},
												]}
											>
												<Input placeholder="Masukkan nama pemberi referral" />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												name={fieldName('kkbRewardPercentage')}
												label="Jumlah Reward KKB?"
												rules={[
													{
														validator: (_, value) => {
															const hasReward = form.getFieldValue(
																fieldName('kkbRewardAvailableId'),
															);
															if (hasReward === 2) {
																// No reward, no validation needed
																return Promise.resolve();
															}
															if (hasReward === 1 && (!value || value <= 0)) {
																return Promise.reject(
																	new Error('Jumlah reward KKB harus diisi'),
																);
															}
															return Promise.resolve();
														},
													},
												]}
											>
												<InputNumber
													disabled={
														viewOnly ||
														form.getFieldValue(
															fieldName('kkbRewardAvailableId'),
														) === 2 // Disabled when "Tidak" (No reward)
													}
													suffix="%"
													controls={false}
													max={25}
													min={0}
													placeholder="0-25%"
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									{/* <Row gutter={40}>
										<Col span={12}>
											<Form.Item
												name={fieldName('hasDealerRefund')}
												label="Apakah ada refund dealer?"
												rules={[rule.required()]}
											>
												<Select
													options={[
														{
															label: 'Ya',
															value: true,
														},
														{
															label: 'Tidak',
															value: false,
														},
													]}
													placeholder="Pilih ya/tidak"
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName('dealerRefundAmount')}
												label="Jumlah Refund Dealer (Rp)"
												rules={[
													{
														validator: (_, value) => {
															const hasRefund = form.getFieldValue(
																fieldName('hasDealerRefund'),
															);
															if (hasRefund === false) {
																return Promise.resolve(); // Field disabled, no validation needed
															}
															if (
																hasRefund === true &&
																(!value || value <= 0)
															) {
																return Promise.reject(
																	new Error('Jumlah refund harus diisi'),
																);
															}
															return Promise.resolve();
														},
													},
												]}
											>
												<InputNumber
													disabled={
														form.getFieldValue(fieldName('hasDealerRefund')) ===
														false
													}
													placeholder="Masukkan jumlah refund"
													style={{ width: '100%' }}
													formatter={(value) =>
														value
															? value
																	.toString()
																	.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
															: ''
													}
													parser={(value) =>
														value ? value.replace(/\D/g, '') : ''
													}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider /> */}
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												label={
													<Fragment>
														<span>
															Jumlah Reward KKB (Rp)
															<br />
															<small style={{ fontWeight: 'normal' }}>
																Per Unit
															</small>
														</span>
													</Fragment>
												}
											>
												<Input
													disabled
													value={
														kkbRewardPerUnit
															? formatCurrency(kkbRewardPerUnit)
															: ''
													}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label={
													<Fragment>
														<span>
															Jumlah Reward KKB (Rp)
															<br />
															<small style={{ fontWeight: 'normal' }}>
																All Unit
															</small>
														</span>
													</Fragment>
												}
											>
												<Input
													disabled
													value={
														kkbRewardAllUnit
															? formatCurrency(kkbRewardAllUnit)
															: ''
													}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Fragment>
							),
						},
						{
							label: 'Cara Pembayaran Angsuran',
							children: (
								<Fragment>
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												name="wayOfPayment"
												label="Way Of Payment"
											>
												<Select
													disabled={view.type === 'view-transaction'}
													options={wayOfPaymentOptions.map((option) => {
														return {
															label: option.value,
															value: option.id,
														};
													})}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												name={fieldName('bankId')}
												noStyle
											/>
											<Form.Item
												name={fieldName('bank')}
												label="Nama Bank"
												rules={[rule.required()]}
												getValueFromEvent={(_, option) => option?.data}
												getValueProps={(value) => {
													if (!value) return {};
													return {
														value: {
															value: value.id,
															label: value.value,
															data: value,
														},
													};
												}}
											>
												<Select
													{...selectMasterBank}
													onSelect={(value, option) => {
														console.log(value, option);
														selectMasterBank!.onSelect?.(value, option);
														form.setFieldValue(fieldName('bankId'), value);
													}}
													onClear={() => {
														form.setFieldValue(fieldName('bankId'), undefined);
													}}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName('bankBranch')}
												label="Nama Cabang"
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												name={fieldName('bankAccountNumber')}
												label="Nomor Rekening"
												rules={[rule.required(), rule.strNumber()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Form.Item
										name={fieldName('wayOfPaymentNotes')}
										label="Notes"
										rules={[rule.required()]}
										labelCol={{ span: 5 }}
										wrapperCol={{ span: 19 }}
									>
										<Input.TextArea rows={4} />
									</Form.Item>
									<Divider />
								</Fragment>
							),
						},
					]}
				/>
				<div
					style={{
						display: 'flex',
						gap: 20,
						justifyContent: 'flex-end',
						marginTop: 20,
					}}
				>
					<Button
						disabled={false}
						style={{
							display: 'block',
							width: 200,
						}}
						onClick={() => {
							setView({
								type: 'main',
							});
						}}
					>
						BACK
					</Button>
					{view.type !== 'view-transaction' && (
						<Button
							type="primary"
							htmlType="submit"
							style={{
								display: 'block',
								width: 200,
							}}
						>
							SAVE
						</Button>
					)}
				</div>
			</Form>
		);
	};

	return (
		<Fragment>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				{typeof onBack === 'function' && (
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => onBack?.()}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
				)}
				<div style={{ flex: 1 }}>
					<FleetTitle>Main Information</FleetTitle>
				</div>
			</div>
			<DataRow
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(customerId);
									}}
								>
									{customerName}
								</a>
							) : (
								'-'
							),
						],
						['No.MKL', mklNo],
					],
					[
						[
							'Group Customer BMPP',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!bmppGroupId) return;
									openCustomerGroupDetail(bmppGroupId);
								}}
							>
								{bmppGroupName}
							</a>,
						],
						['Kelas Industri', industryClass],
					],
					[
						['Portofolio Guideline', portfolioGuideline],
						['Ultimate Owner', ultimateOwner],
					],
					[
						[
							'Summary NAP',
							napPlafonTypeId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!prospectId) return;

										if (napPlafonTypeId === 1) {
											napNJFModal.open({
												prospectId,
											});
										}

										if (napPlafonTypeId === 2) {
											napJFModal.open({
												prospectId,
											});
										}
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
						[
							'Summary Risalah',
							resolutionFile ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(resolutionFile);
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'View Kolektabilitas',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!slikInquiryId) {
										modal.confirm({
											title:
												'Customer SLIK data is not available. Please try again.',
											cancelText: 'OK',
											okButtonProps: { style: { display: 'none' } },
										});
										return;
									}
									kolektabilitasModal.open({
										slikInquiryId: slikInquiryId!,
									});
								}}
							>
								View Kolektabilitas
							</a>,
						],
					],
				]}
			/>
			{renderMainForm()}
			{renderTransactionForm()}

			<NAPNJFModal {...napNJFModal} />
			<NAPJFModal {...napJFModal} />
			<CovenantModal
				{...covenantModal}
				{...{
					customerId,
					prospectId,
					categoryOptions,
					priorityOptions,
					repeatFrequencyOptions,
					customRepeatFrequencyOptions,
					monitoringType,
					furtherAnalysisOptions,
					monthOptions,
					positionOptions,
					dayOfWeekOptions,
					statusOptions,
				}}
			/>

			<SimulationModal {...simulationModal} />
			<AssetModal {...assetModal} />
			<FinancialReportModal {...financialReportModal} />
			<BankStatementModal {...bankStatementModal} />
			<OtherDocumentModal {...otherDocumentModal} />
			<SPKDocumentModal {...spkDocumentModal} />
			{slikInquiryId && <KolektabilitasModal {...kolektabilitasModal} />}
		</Fragment>
	);
};

export default MPPPage;
